import { CardType, GameState, PreparedCard, CardColor, ActivationAttempt, ClassId, Card, Player, GamePhase, HeroId } from '../types'
import { getEffectiveHero } from './heroes'
import { v4 as uuidv4 } from 'uuid'
import { ABSOLUTE_MAX_PREPARED_CARDS } from '../cardCatalog'
import { getAvailablePrepareSlots, getEffectiveEssenceCostForPlayer, isImmuneToDamage } from './gameLogic'
import { analyzeOpponent, canOpponentCounter, getOptimalAttackWindow, getCardEffect } from './ai_perfect_info'
import { canCounterblastTarget, isImmediateOrReflectedDamageSource, isSpikesCard } from './damageRules'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}


const isShieldEffect = (type: CardType): boolean => {
  return [
    CardType.Hibernation,
    CardType.Dispel,
    CardType.Pushback,
    CardType.Defense,
    CardType.PsyBlock,
    CardType.Frost,
    CardType.Invincible,
  ].includes(type)
}

// Helper to identify defensive stash cards (Shimmering Shield, Counterblast, Invincible, Spikes, Seal of Silence)
export const isDefensiveStash = (card: { type: CardType; subType?: string; faceSlug?: string }): boolean => {
  if (card.type === CardType.Defense) return true // Shimmering Shield
  if (card.type === CardType.Counterblast) return true
  if (card.type === CardType.Invincible) return true
  if (card.type === CardType.SealOfSilence) return true
  if (card.subType === 'Armory' && (card as any).faceSlug === 'spikes') return true
  return false
}

// Helper to identify aggressive stash cards (Damage dealers)
export const isAggressiveStash = (card: { type: CardType }): boolean => {
  return [
    CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
    CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
    CardType.Chromabow, CardType.PlantedBomb, CardType.Trembling
  ].includes(card.type)
}

const getTopActivation = (state: GameState): ActivationAttempt | undefined => {
  // Find the last unresolved activation
  for (let i = state.activations.length - 1; i >= 0; i--) {
    if (!state.activations[i].resolved) {
      return state.activations[i]
    }
  }
  return undefined
}

const damageTypes = [
  CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
  CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
  CardType.Chromabow, CardType.Trembling
]

export type AIAction =
  | { type: 'PASS'; priority: number }
  | { type: 'ATTEMPT_ACTIVATION'; cardId: string; rotation: number; priority: number; targetId?: number }
  | { type: 'COUNTER_ACTIVATION'; cardId: string; rotation: number; priority: number; targetActivationId?: string }
  | { type: 'ACCEPT_ACTIVATION'; priority: number }
  | { type: 'DISCARD_STASH'; cardId: string; priority: number }
  | { type: 'DISCARD_HAND_FOR_ESSENCE'; cardId: string; priority: number }
  | { type: 'START_TOWER_PURCHASE'; priority: number }
  | { type: 'PURCHASE_TOWER'; color: CardColor; priority: number }
  | { type: 'OPEN_TOWER_CHANGE_MENU'; towerIndex: number; priority: number }
  | { type: 'CHANGE_TOWER_COLOR'; color: CardColor; towerIndex: number; priority: number }
  | { type: 'PREPARE_CARD'; cardId: string; rotation: number; priority: number }
  | { type: 'ROTATE_PREPARED'; slotIndex: number; priority: number }
  | { type: 'MAGE_CAST_SPELL'; cardId: string; priority: number; rotation?: number; targetId?: number; targetActivationId?: string }
  | { type: 'ASTRID_BLAST'; color: CardColor; priority: number; targetId: number }
  | { type: 'CHOOSE_CHROMABOW_COLOR'; color: CardColor; priority: number }
  | { type: 'CHOOSE_HIBERNATION_COLOR'; color: CardColor; priority: number }
  | { type: 'CHOOSE_BURGLARY_CARD'; targetCardId: string; priority: number }
  | { type: 'CHOOSE_PURIFICATION_TARGET'; targetColor: CardColor; priority: number }
  | { type: 'CHOOSE_LOCKED_CARD_RETURN'; cardId: string; priority: number }
  | { type: 'COMPLETE_BROKEN_SECRET'; priority: number }


const requiredCounterColorForClass = (cls?: ClassId): CardColor | null => {
  if (!cls) return null
  if (cls === 'MAGE') return CardColor.Pink
  if (cls === 'OCCULTIST') return CardColor.Black
  if (cls === 'WARRIOR') return CardColor.Red
  return null
}

const countUsableColorCards = (state: GameState, playerId: number, color: CardColor): number => {
  const player = state.players.find(p => p.id === playerId)!
  return player.hand.filter(c => c.color === color && !c.essenceCost).length
}

const pickDiscardForEssence = (state: GameState, playerId: number): string | null => {
  const player = state.players.find(p => p.id === playerId)!
  const opponent = state.players.find(p => p.id !== playerId)!
  const neededCounterColor = requiredCounterColorForClass(opponent.playerClass)
  const round = state.round

  // NOTE: Stash cards CANNOT be burned from hand - only from prepared!
  // This function only handles normal color cards.

  // Normal cards - only burn if round > 2 and we have enough of that color
  const normalCards = player.hand.filter(c => !c.essenceCost)
  if (round <= 2) return null

  // Score normal cards - burn the WORST one
  const scoredNormal = normalCards.map(c => {
    let score = c.value || 0

    // Counter-color cards are VERY valuable - don't burn them
    if (neededCounterColor && c.color === neededCounterColor) {
      score += 20
    }
    // Cards we have many of are less valuable (only burn if 2+ of this color)
    const colorCount = countUsableColorCards(state, playerId, c.color)
    if (colorCount < 2) {
      score += 15 // Don't burn if only one of this color
    }

    return { card: c, score }
  }).sort((a, b) => a.score - b.score) // Sort ascending - worst cards first

  // Only burn if we have a "safe" card (low score)
  const safeToBurn = scoredNormal.find(item => item.score < 15)
  return safeToBurn ? safeToBurn.card.id : null
}


const pickStashToDiscard = (state: GameState, playerId: number): string | null => {
  const player = state.players.find(p => p.id === playerId)!
  const opponent = state.players.find(p => p.id !== playerId)!
  const opponentClassSubType = opponent.playerClass ? (opponent.playerClass === 'WARRIOR' ? 'Armory' : opponent.playerClass === 'MAGE' ? 'Spell' : 'Artifact') : undefined;

  const choices = player.prepared.filter(pc => !!pc.card.essenceCost)
  if (choices.length === 0) return null

  const scored = choices.map(pc => {
    let s = scoreCardForAI(pc.card, player, opponent, new Set(), state)
    // NORIM Logic: Bonus for burning opponent's class stash cards
    if (player.hero === 'NORIM' && opponentClassSubType && pc.card.subType === opponentClassSubType) {
      s += 15;
    }
    // Extra burn priority for low-winrate stash cards
    const lowWRSlugs = new Set(['voodoo-doll', 'life-charger', 'cursed-iron', 'ale', 'broken-secret', 'steel-shield', 'seal-of-silence', 'fortunes-favor', 'madness-control', 'spikes', 'arcane-boots', 'body-upgrade'])
    if ((pc.card as any).faceSlug && lowWRSlugs.has((pc.card as any).faceSlug)) s -= 5
    return { id: pc.card.id, score: s }
  })
  scored.sort((a, b) => a.score - b.score)
  return scored[0].id
}


const estimateIncomingDamage = (state: GameState, attackerId: number, defenderId: number): number => {
  const attacker = state.players.find(p => p.id === attackerId)!
  const defender = state.players.find(p => p.id === defenderId)!
  const damageTypesInternal = damageTypes;
  const defenderIsImmune = isImmuneToDamage(defender, state.round)
  if (defenderIsImmune) return 0
  const reduction = defender.effectCards.filter(c => c.type === CardType.CursedIron).reduce((a, c) => a + (c.value || 0), 0)

  let totalDmg = 0
  // Sum PREPARED attacks
  attacker.prepared.forEach(pc => {
    const eff = getCardEffect(pc)
    if (damageTypesInternal.includes(eff.type)) {
      totalDmg += Math.max(0, (eff.value || 0) - reduction)
    }
  })
  // Sum ACTIVATING attacks (already in stack)
  state.activations.filter(act => !act.resolved && act.attackingCard.card.ownerId === attackerId).forEach(act => {
    const eff = getCardEffect(act.attackingCard)
    if (damageTypesInternal.includes(eff.type)) {
      totalDmg += Math.max(0, (eff.value || 0) - reduction)
    }
  })

  return Math.max(0, totalDmg - defender.shield)
}

const isLethalDOTNextDay = (player: Player): boolean => {
  const diseaseCount = player.effectCards.filter(c => c.type === CardType.Disease).length
  const naturalGripCount = player.effectCards.filter(c => c.type === CardType.NaturalGrip).length
  const dotDamage = diseaseCount + (naturalGripCount * 2)
  return dotDamage >= player.health
}

const damageFromPreparedCard = (state: GameState, defender: { health: number; shield: number; effectCards: any[] }, pc: PreparedCard): number => {
  const eff = getCardEffect(pc)
  if (!damageTypes.includes(eff.type)) return 0
  const defenderIsImmune = isImmuneToDamage(defender as any, state.round)
  if (defenderIsImmune) return 0
  const reduction = defender.effectCards.filter((c: any) => c.type === CardType.CursedIron).reduce((a: number, c: any) => a + (c.value || 0), 0)
  const finalAttackValue = Math.max(0, (eff.value || 0) - reduction)
  return Math.max(0, finalAttackValue - defender.shield)
}

/**
 * Counter-Chain Prediction: Calculate probability of attack success based on opponent's counters
 */
const predictCounterChainOutcome = (
  state: GameState,
  ai: Player,
  opponent: Player,
  attackCard: PreparedCard
): { successProbability: number; expectedDamage: number } => {
  const analysis = analyzeOpponent(state, ai.id)
  const attackEffect = getCardEffect(attackCard)
  const baseDamage = attackEffect.value || 0

  // Check if attack is completely blocked
  if (analysis.hasInvincible || analysis.handHasInvincible) {
    // Check if AI has counter for Invincible (DoubleCross)
    const aiHasDoubleCross = ai.prepared.some(pc =>
      getCardEffect(pc).type === CardType.DoubleCross
    )
    if (!aiHasDoubleCross) return { successProbability: 0, expectedDamage: 0 }
  }

  // Check for Defense counters (including hand)
  if (analysis.canCounterAttack || analysis.handCanCounterAttack) {
    // Check if AI can counter the counter
    const aiHasCounterCounter = ai.prepared.some(pc => {
      const eff = getCardEffect(pc)
      return eff.type === CardType.Counterblast ||
        eff.type === CardType.DoubleCross ||
        eff.type === CardType.Pushback
    })

    if (analysis.hasCounterblast || analysis.handCanCounterAttack && opponent.hand.some(c => c.type === CardType.Counterblast || c.typeB === CardType.Counterblast)) {
      // Counterblast reflects damage - risky!
      if (!aiHasCounterCounter) return { successProbability: 0.2, expectedDamage: baseDamage * 0.2 }
    }

    if (analysis.preparedCardTypes.has(CardType.Defense) ||
      analysis.preparedCardTypes.has(CardType.Hibernation) ||
      analysis.preparedCardTypes.has(CardType.Protector)) {
      if (!aiHasCounterCounter) return { successProbability: 0.4, expectedDamage: baseDamage * 0.4 }
    }
  }

  // Check subtype-specific counters
  const subType = attackCard.card.subType as 'Armory' | 'Spell' | 'Artifact' | undefined
  if (subType && canOpponentCounter(analysis, attackEffect.type, subType)) {
    const counterProbability = 0.6 // Reduced chance
    return { successProbability: counterProbability, expectedDamage: baseDamage * counterProbability }
  }

  // No counters detected - high success
  return { successProbability: 1.0, expectedDamage: baseDamage }
}

/**
 * Calculate if AI has potential lethal damage this night/next day
 * Includes: direct attacks + Disease/NaturalGrip effects
 * Includes damage from already active activations
 */
export const calculatePotentialLethalDamage = (
  state: GameState,
  ai: Player,
  opponent: Player
): { totalPotentialDamage: number; canKillThisTurn: boolean; canKillByNextDay: boolean; isLethalSafe: boolean; canKillInstantly: boolean } => {
  const analysis = analyzeOpponent(state, ai.id)

  // 1. Direct attack damage from prepared cards (ESSENCE AWARE)

  let totalDirectDamage = 0
  const opponentIsImmune = isImmuneToDamage(opponent, state.round)
  const cursedIronReduction = opponent.effectCards
    .filter(c => c.type === CardType.CursedIron)
    .reduce((acc, c) => acc + (c.value || 0), 0)

  if (!opponentIsImmune) {
    let rawTotal = 0
    let currentEssence = ai.essence;

    // Check if we can gain more essence by burning stash cards we DON'T need for lethal
    // Also exclude LOCKED stash cards from being burned (can't burn locked cards)
    const nonDamageStash = ai.prepared.filter(pc =>
      pc.card.essenceCost &&
      !damageTypes.includes(getCardEffect(pc).type) &&
      !(pc.lockedState && state.round - pc.lockedState.appliedRound === 0)
    );
    currentEssence += nonDamageStash.length * 4;

    ai.prepared.forEach(pc => {
      // Skip locked cards
      if (pc.lockedState && state.round - pc.lockedState.appliedRound === 0) return;

      const effect = getCardEffect(pc)
      if (damageTypes.includes(effect.type)) {
        const cost = getEffectiveEssenceCostForPlayer(pc.card, ai);
        if (currentEssence >= cost) {
          rawTotal += Math.max(0, (effect.value || 0) - cursedIronReduction)
          currentEssence -= cost;
        }
      }
    })

    // 1.1 Mage Spells in Hand (damageTypes aware)
    if (ai.playerClass === 'MAGE') {
      ai.hand.forEach(c => {
        if (c.subType === 'Spell' && c.essenceCost) {
          const cost = getEffectiveEssenceCostForPlayer(c, ai);
          // Only count if we have remaining essence AFTER prepared cards (simple heuristic)
          // or if we prioritized this spell over a prepared card (hard to calc perfect knapsack here)
          // For now: assume we use available essence
          if (currentEssence >= cost) {
            const eff = getCardEffect({ card: c, rotation: 0 });
            if (damageTypes.includes(eff.type)) {
              rawTotal += Math.max(0, (eff.value || 0) - cursedIronReduction);
              currentEssence -= cost;
            }
          }
        }
      });
    }

    // 1.2 Astrid Hero Ability
    if (ai.hero === 'ASTRID' && ai.astridCharges) {
      // Each 3 charges = 1 blast (2 damage)
      // Check red/purple/black charges
      Object.values(ai.astridCharges).forEach((count: any) => {
        if (count >= 3) {
          rawTotal += Math.max(0, 2 - cursedIronReduction); // Blast deals 2 dmg
        }
      });
    }

    // 2. Add damage already in the activation stack (if initiated by AI)
    let stackDamage = 0;
    state.activations.forEach(act => {
      if (!act.resolved && act.attackingCard.card.ownerId === ai.id) {
        const effect = getCardEffect(act.attackingCard);
        if (damageTypes.includes(effect.type)) {
          stackDamage += Math.max(0, (effect.value || 0) - cursedIronReduction);
        }
      }
    });

    totalDirectDamage = Math.max(0, rawTotal + stackDamage - opponent.shield)

    // 2.1 Calculate INSTANT lethal (using only current essence, no burning)
    // This is what AI can deal RIGHT NOW without any intermediate actions
    let instantDamage = 0;
    let instantEssence = ai.essence;
    ai.prepared.forEach(pc => {
      if (pc.lockedState && state.round - pc.lockedState.appliedRound === 0) return;
      const effect = getCardEffect(pc);
      if (damageTypes.includes(effect.type)) {
        const cost = getEffectiveEssenceCostForPlayer(pc.card, ai);
        if (instantEssence >= cost) {
          instantDamage += Math.max(0, (effect.value || 0) - cursedIronReduction);
          instantEssence -= cost;
        }
      }
    });
    instantDamage = Math.max(0, instantDamage + stackDamage - opponent.shield);
    var canKillInstantly = instantDamage >= opponent.health;
  }

  // Disease/NaturalGrip damage (applies at start of next day)
  const opponentDiseaseCount = opponent.effectCards.filter(c => c.type === CardType.Disease).length
  const opponentHasNaturalGrip = opponent.effectCards.filter(c => c.type === CardType.NaturalGrip).length
  const dotDamage = opponentDiseaseCount + (opponentHasNaturalGrip * 2)

  // Check if AI has Disease/NaturalGrip prepared to add more
  let additionalDOT = 0
  ai.prepared.forEach(pc => {
    const effect = getCardEffect(pc)
    if (effect.type === CardType.Disease) additionalDOT += 1
    if (effect.type === CardType.NaturalGrip) additionalDOT += 2
  })

  const totalPotentialDamage = totalDirectDamage + dotDamage + additionalDOT
  const canKillThisTurn = totalDirectDamage >= opponent.health
  const canKillByNextDay = totalPotentialDamage >= opponent.health

  // SAFETY CHECK: Is it safe to enter Bloodlust?
  const hasTrap = opponent.prepared.some(pc => {
    const eff = getCardEffect(pc);
    return eff.type === CardType.Counterblast || (pc.card.subType === 'Armory' && (pc.card as any).faceSlug === 'spikes');
  });

  return { totalPotentialDamage, canKillThisTurn, canKillByNextDay, isLethalSafe: !hasTrap, canKillInstantly: canKillInstantly ?? false }
}

/**
 * Get optimal tower color for initial selection or Purification
 * Priority: Counter opponent class > Black > Purple > Red > Green > Yellow > Pink
 * For Purification: prioritize stealing opponent towers first
 */
export const getOptimalTowerColor = (
  state: GameState,
  aiId: number,
  isPurification: boolean = false
): CardColor => {
  const ai = state.players.find(p => p.id === aiId)!
  const opponent = state.players.find(p => p.id !== aiId)!

  // Default priority order (when opponent class unknown)
  const defaultPriority: CardColor[] = [
    CardColor.Black,   // PsyBlock - blocks Artifacts
    CardColor.Purple,  // Steal - card advantage
    CardColor.Red,     // Attack/Pushback - damage and counter
    CardColor.Green,   // Heal/Disease - sustain
    CardColor.Yellow,  // Energize/Shock - utility
    CardColor.Pink     // Hibernation/Dispel - defensive
  ]

  // Counter-class priorities
  const counterColorForClass = requiredCounterColorForClass(opponent.playerClass)

  // For Purification: prioritize stealing from opponent
  if (isPurification && opponent.towers.length > 0) {
    // Steal opponent tower closest to our default priority
    const opponentTowerColors = opponent.towers.map(t => t.color)
    for (const color of defaultPriority) {
      if (opponentTowerColors.includes(color)) return color
    }
    // If no priority match, take any opponent tower
    return opponent.towers[0].color
  }

  // For initial tower or Purification when opponent has no towers
  // Available = neutral or opponent towers
  const availableTowers = [
    ...state.availableTowers,
    ...opponent.towers.map(t => t.color)
  ]

  // First priority: counter opponent class if available
  if (counterColorForClass && availableTowers.includes(counterColorForClass)) {
    return counterColorForClass
  }

  // Second priority: follow default order
  for (const color of defaultPriority) {
    if (availableTowers.includes(color)) return color
  }

  // Fallback: any available tower
  return availableTowers[0] || CardColor.Black
}




export const chooseBestStashCardId = (state: GameState, playerId: number, options: { id: string; type: CardType; essenceCost?: number; subType?: 'Armory' | 'Spell' | 'Artifact'; value?: number }[]): { id: string, fromBurned: boolean } => {
  const player = state.players.find(p => p.id === playerId)!
  const opponent = state.players.find(p => p.id !== playerId)!
  const round = state.round

  // Game phase definitions
  const earlyGame = round <= 3
  const lateGame = round >= 4

  // Class-to-subType mapping
  const classToSubType: Record<string, 'Armory' | 'Spell' | 'Artifact'> = {
    'WARRIOR': 'Armory',
    'MAGE': 'Spell',
    'OCCULTIST': 'Artifact'
  }

  const aiClassSubType = player.playerClass ? classToSubType[player.playerClass] : undefined
  const opponentClassSubType = opponent.playerClass ? classToSubType[opponent.playerClass] : undefined

  // Decisive card types
  const decisiveTypes = [CardType.Counterblast, CardType.FlyingScythe]
  const canBeLethal = (c: { type: CardType; value?: number }): boolean => {
    if (!isAggressiveStash(c)) return false
    return (c.value || 0) >= opponent.health
  }

  // Check opponent aggression (Did they prepare aggressive stash cards?)
  // Note: We check prepared cards. "Activated" is harder to track without history, but prepared is a good proxy for intent.
  const opponentHasAggressiveStash = opponent.prepared.some(pc =>
    !!pc.card.essenceCost && isAggressiveStash(pc.card)
  )

  // Check incoming damage (for early game defense logic)
  const incomingDamage = estimateIncomingDamage(state, opponent.id, player.id)
  const isThreat = incomingDamage > 0 || opponentHasAggressiveStash

  // Logic for Defensive Cards Penalty/Bonus - NIGHT LOGIC (Stash Selection)
  // "ночью ... -X" values apply here
  let defenseModifier = 0

  // Case 1: No Threat (Safe)
  if (!isThreat) {
    if (earlyGame) defenseModifier = -8 // "Ранняя игра + Нет входящего урона... ночью до -8"
    else defenseModifier = -5           // "ночью -5"
  }
  // Case 2: Threat Exists
  else {
    if (earlyGame) defenseModifier = -2 // "Ранняя игра + есть входящий урон... ночью -2"
    else defenseModifier = -1           // "Поздняя игра + есть входящий урон... ночью -1"
  }

  // Score calculation function
  const scoreCard = (c: { id: string; type: CardType; essenceCost?: number; subType?: string; value?: number }) => {
    let s = 0

    // Base score from card value
    s += (c.value || 0)

    // === OCCULTIST DISCOUNT ADVANTAGE ===
    const isOccultist = player.playerClass === 'OCCULTIST';
    if (c.essenceCost) {
      const effCost = getEffectiveEssenceCostForPlayer(c as any, player);
      const discount = c.essenceCost - effCost;
      if (discount > 0) {
        s += discount * 2; // High reward for "big savings" (e.g. 5 essence saving = +10 score)
        s += 5; // Flat bonus for class synergy
      }
    }

    // === DEFENSE LOGIC ===
    if (isDefensiveStash(c)) {
      s += defenseModifier
    }

    if (earlyGame) {
      // === EARLY GAME (turns 1-3) ===
      if (aiClassSubType && c.subType === aiClassSubType) s += 7
      else if (aiClassSubType && c.subType && c.subType !== aiClassSubType) s -= 5

      // Burning logic protects Decisive and Class cards regardless of cost.
      // Apply penalty only if NOT a decisive card AND NOT an AI class card
      const isDecisiveOrLethal = decisiveTypes.includes(c.type) || canBeLethal(c);
      const isAiClassCard = aiClassSubType && c.subType === aiClassSubType;

      // Use EFFECTIVE cost for penalty
      const effCost = getEffectiveEssenceCostForPlayer(c as any, player);
      if (effCost >= 7 && !isDecisiveOrLethal && !isAiClassCard) {
        s -= 3;
      }

      if (opponentClassSubType && c.subType === opponentClassSubType) s += 5

      if (isDecisiveOrLethal) s += 3
    } else if (lateGame) {
      // === LATE GAME (turns 4-5) ===
      if (aiClassSubType && c.subType === aiClassSubType) s += 7
      else if (aiClassSubType && c.subType && c.subType !== aiClassSubType) s -= 5

      // No expensive card penalty in late game

      if (opponentClassSubType && c.subType === opponentClassSubType) s += 5

      if (decisiveTypes.includes(c.type) || canBeLethal(c)) s += 3
    }

    // === WINRATE-BASED ADJUSTMENTS ===
    // High-winrate stash cards get a bonus, low-winrate cards get a penalty
    const faceSlug = (c as any).faceSlug as string | undefined
    const highWRSlugs = new Set(['natural-grip', 'fireball', 'shadow-burglary', 'counterblast', 'eyes-of-madness', 'trembling', 'purification', 'sword-of-revenge', 'life-drain', 'flying-scythe', 'inferno-gauntlet'])
    const lowWRSlugs = new Set(['voodoo-doll', 'life-charger', 'cursed-iron', 'ale', 'broken-secret', 'steel-shield', 'seal-of-silence', 'fortunes-favor', 'madness-control', 'spikes'])
    if (faceSlug && highWRSlugs.has(faceSlug)) s += 4
    if (faceSlug && lowWRSlugs.has(faceSlug)) s -= 4

    return s
  }

  // Score normal options with random tiebreaker
  const scoredNormal = options.map(c => ({
    id: c.id,
    score: scoreCard(c),
    random: Math.random(),
    fromBurned: false
  }))

  // FERRIM: Also score burned stash cards
  const burnedCards = state.burnedStashCards || []
  let scoredBurned: { id: string; score: number; random: number; fromBurned: boolean }[] = []
  if (player.hero === 'FERRIM' && burnedCards.length > 0) {
    scoredBurned = burnedCards.map(c => ({
      id: c.id,
      score: scoreCard({
        id: c.id,
        type: c.type,
        essenceCost: c.essenceCost,
        subType: c.subType as 'Armory' | 'Spell' | 'Artifact' | undefined,
        value: c.value
      }) + 3, // Slight bonus for burned cards
      random: Math.random(),
      fromBurned: true
    }))
  }

  // Combine and sort
  const allScored = [...scoredNormal, ...scoredBurned].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    return b.random - a.random
  })

  const best = allScored[0] || { id: options[0]?.id || '', fromBurned: false }

  return { id: best.id, fromBurned: best.fromBurned }
}

export const chooseLockedCardToReturnFromOverflow = (state: GameState, playerId: number): string | null => {
  const player = state.players.find(p => p.id === playerId)
  if (!player || state.overflowMaxSlots === undefined) return null

  const lockedCards = player.prepared.filter(pc => pc.lockedState)
  if (lockedCards.length <= state.overflowMaxSlots) return null

  const scoredCards = lockedCards
    .map(pc => {
      const effect = getCardEffect(pc)
      let score = 0
      if (pc.card.essenceCost) score += 20

      switch (effect.type) {
        case CardType.Attack:
          score += effect.value * 2
          break
        case CardType.FortunesFavor:
          score += 50
          break
        case CardType.BodyUpgrade:
          score += 40
          break
        case CardType.StealTower:
          score += 30
          break
        case CardType.Defense:
          score += effect.value * 1.5
          break
        case CardType.Heal:
          score += effect.value
          break
        default:
          score += 5
      }

      return { id: pc.card.id, score }
    })
    .sort((a, b) => a.score - b.score)

  return scoredCards[0]?.id || null
}


const calculateMaxPotentialEssence = (state: GameState, playerId: number): number => {
  const player = state.players.find(p => p.id === playerId)!
  let potential = player.essence

  // Burning prepared stash (+4 each)
  potential += player.prepared.filter(pc => !!pc.card.essenceCost).length * 4

  // Discarding color cards from hand (+1 each)
  potential += player.hand.filter(c => !c.essenceCost).length * 1

  return potential
}

const pickBestJustInTimeDiscard = (state: GameState, playerId: number, neededEssence: number, priority: number): AIAction | null => {
  const player = state.players.find(p => p.id === playerId)!

  // 1. DISCARD-AWARE: If we only need a small amount of essence, use hand cards (grainy +1) 
  // instead of stash cards (block +4) to avoid wasting essence value.
  const handChoices = player.hand.filter(c => !c.essenceCost)
  if (handChoices.length >= neededEssence && handChoices.length > 0) {
    const bestHandId = pickDiscardForEssence(state, playerId)
    if (bestHandId) return { type: 'DISCARD_HAND_FOR_ESSENCE', cardId: bestHandId, priority }
    // Fallback if scoring returned null but we NEED it
    return { type: 'DISCARD_HAND_FOR_ESSENCE', cardId: handChoices[0].id, priority }
  }

  // 2. Otherwise, use stash choices (+4 each) if available
  const stashChoices = player.prepared.filter(pc => !!pc.card.essenceCost)
  if (stashChoices.length > 0) {
    const bestStashId = pickStashToDiscard(state, playerId)
    if (bestStashId) return { type: 'DISCARD_STASH', cardId: bestStashId, priority }
  }

  // 3. Fallback: if we still need essence and stash is empty, use any remaining hand cards
  if (handChoices.length > 0) {
    const bestHandId = pickDiscardForEssence(state, playerId)
    if (bestHandId) return { type: 'DISCARD_HAND_FOR_ESSENCE', cardId: bestHandId, priority }
    return { type: 'DISCARD_HAND_FOR_ESSENCE', cardId: handChoices[0].id, priority }
  }

  return null
}

const computeEssenceGainOptions = (state: GameState, playerId: number): { preparedId?: string; normalId?: string } => {
  const player = state.players.find(p => p.id === playerId)!
  const prepared = player.prepared.find(pc => !!pc.card.essenceCost)
  const normal = player.hand.find(c => !c.essenceCost)
  const preparedId = prepared ? prepared.card.id : undefined
  const normalId = normal ? normal.id : undefined
  return { preparedId, normalId }
}

const canEssenceGainEnablePrepared = (state: GameState, playerId: number, extraEssence: number): boolean => {
  const player = state.players.find(p => p.id === playerId)!
  const current = player.essence
  return player.prepared.some(pc => pc.card.essenceCost && pc.card.essenceCost! > current && pc.card.essenceCost! <= current + extraEssence)
}

export const runAIDayActions = (state: GameState): GameState => {
  log('aiDay', 'Running AI Day Actions...')
  log('aiDay', 'AI Day Actions complete.')
  return state
}

/**
 * Adaptive Priority Weights: Compute dynamic bonuses based on game state
 */
const computeAdaptiveWeights = (state: GameState, ai: Player, opponent: Player): {
  attackBonus: number;
  defenseBonus: number;
  healBonus: number;
  essenceBonus: number;
} => {
  const round = state.round;
  const healthRatio = ai.health / ai.maxHealth;
  const opponentHealthRatio = opponent.health / opponent.maxHealth;
  const towersToWin = 5 - ai.towers.length;
  const opponentTowersToWin = 5 - opponent.towers.length;

  // Aggressive in late-game or when close to finishing opponent
  const attackBonus =
    (round > 4 ? 20 : 0) +
    (opponentHealthRatio < 0.3 ? 40 : 0) +  // Finish them!
    (towersToWin <= 1 ? -30 : 0);           // Focus towers if close to tower victory

  // Defensive when low HP or opponent is aggressive
  const defenseBonus =
    (healthRatio < 0.4 ? 30 : 0) +
    (opponent.prepared.length > 3 ? 15 : 0);

  // Heal critical at low HP
  const healBonus = healthRatio < 0.25 ? 50 : 0;

  // Essence more important in mid-game
  const essenceBonus = (round >= 2 && round <= 4 && ai.essence < 4) ? 20 : 0;

  return { attackBonus, defenseBonus, healBonus, essenceBonus };
};

export const scoreCardForAI = (
  card: Card,
  ai: Player,
  opponent: Player,
  opponentPreparedTypes: Set<CardType>,
  state?: GameState  // Optional for backward compatibility
): number => {
  let s = 0;
  const { type, value = 0 } = card;

  // Compute adaptive weights if state is provided
  const weights = state ? computeAdaptiveWeights(state, ai, opponent) :
    { attackBonus: 0, defenseBonus: 0, healBonus: 0, essenceBonus: 0 };

  // Helper for "Cheating" checks (AI knows opponent hand)
  // In runAIPreparationTurn this was explicit. Here we re-derive or approximate.
  const opponentHandHasSpell = opponent.hand.some(c => c.subType === 'Spell');
  const opponentHandHasArmory = opponent.hand.some(c => c.subType === 'Armory');
  const opponentHandHasArtifact = opponent.hand.some(c => c.subType === 'Artifact');
  const opponentHandTypes = new Set(opponent.hand.flatMap(c => [c.type, ...(c.typeB ? [c.typeB] : [])]));
  const hasStashPlan = ai.hand.some(c => !!c.essenceCost) || ai.prepared.some(pc => !!pc.card.essenceCost);

  // === CLASS SYNERGY / COUNTER-STRATEGY ===
  const classToSubType: Record<string, string> = {
    'WARRIOR': 'Armory',
    'MAGE': 'Spell',
    'OCCULTIST': 'Artifact'
  }
  const aiClassSubType = ai.playerClass ? classToSubType[ai.playerClass] : undefined
  const opponentClassSubType = opponent.playerClass ? classToSubType[opponent.playerClass] : undefined

  if (aiClassSubType && card.subType === aiClassSubType) {
    s += 7 // Matching own class
  } else if (aiClassSubType && card.subType && card.subType !== aiClassSubType) {
    s -= 5 // Mismatch
  }

  if (opponentClassSubType && card.subType === opponentClassSubType) {
    s += 5 // Matching opponent's class (for deny/counter)
  }

  // === CLASS SPECIFIC BONUSES (Roleplay/Strategy) ===
  const isOccultist = ai.playerClass === 'OCCULTIST';
  const isMage = ai.playerClass === 'MAGE';
  const isWarrior = ai.playerClass === 'WARRIOR';

  // Hero Identities - Use getEffectiveHero for Verisk support
  const effectiveHero = getEffectiveHero(ai, opponent);
  const isShanu = effectiveHero === 'SHANU';
  const isCoril = effectiveHero === 'CORIL';
  const isAstrid = effectiveHero === 'ASTRID';
  const isErokBuru = effectiveHero === 'EROK_BURU';
  const isEvelion = effectiveHero === 'EVELION';
  const isGemata = effectiveHero === 'GEMATA';
  const isNyrix = effectiveHero === 'NYRIX';
  const isTerrik = effectiveHero === 'TERRIK';

  // Occultist loves Artifacts and Control
  if (isOccultist) {
    if (card.subType === 'Artifact') s += 5; // General Artifact Affinity
    if (card.type === CardType.PsyBlock) s += 8;
    if (card.type === CardType.PlantedBomb) s += 10;
    if (card.type === CardType.DiscerningEye) s += 8;

    // Systematic boost for discount benefit
    if (card.essenceCost) {
      const effCost = getEffectiveEssenceCostForPlayer(card, ai);
      const savings = card.essenceCost - effCost;
      if (savings > 0) {
        s += Math.floor(savings * 1.5); // Incentivize picking expensive artifacts
      }
    }
  }

  // NYRIX logic: Green Tower & Green Card focus
  if (isNyrix && card.color === CardColor.Green) {
    s += 8;
  }
  if (isNyrix && (type === CardType.Purification || type === CardType.StealTower)) {
    s += 12;
  }

  // TERRIK logic: Card Cyclone focus
  if (isTerrik && type === CardType.CardCyclone) {
    s += 25;
  }

  // Body Upgrade bonus for Erok/Evelion
  if ((isErokBuru || isEvelion) && type === CardType.BodyUpgrade) {
    const effCost = getEffectiveEssenceCostForPlayer(card, ai);
    if (ai.essence >= effCost) s += 20; // Bonus if playable tonight
  }

  if (isErokBuru && type === CardType.ThunderSprint && (state?.round || 0) <= 3) {
    s += 12; // Early game priority
  }

  if (isEvelion) {
    if (type === CardType.Attack) s += 6; // Aggressive
    if (isShieldEffect(type)) s -= 5; // Less defensive
  }

  if (isGemata && (type === CardType.Purification || type === CardType.StealTower)) {
    s += 15; // Gemata loves purification
  }

  // SHANU logic: Consolidated into Hibernation switch case below

  // CORIL Logic: Tower choice early game bias (handled in tower choice)
  // Preparation focus: counter reactions (handled in switch/case)

  // Mage loves Spells and Dispel
  if (isMage) {
    if (card.type === CardType.Dispel) s += 4;
    if (card.type === CardType.SealOfSilence) s += 3;
    if (card.type === CardType.MageTower) s += 5;
  }

  // Warrior loves Weapons and Pushback
  if (isWarrior) {
    if (card.type === CardType.Pushback) s += 4;
    if (card.type === CardType.DoubleCross) s += 3;
  }

  switch (type) {
    case CardType.Attack:
      s = 10 + value + weights.attackBonus
      if (opponentPreparedTypes.has(CardType.Defense) || opponentPreparedTypes.has(CardType.Hibernation) || opponentPreparedTypes.has(CardType.Invincible) || opponentPreparedTypes.has(CardType.Pushback) || opponentPreparedTypes.has(CardType.Counterblast) || opponentPreparedTypes.has(CardType.Protector)) s -= 6

      // RED CARD CONSERVATION: Lower Attack priority if not lethal, save for Pushback
      // Attack is only worth using if it contributes to lethal damage
      if (card.color === CardColor.Red && state) {
        const lethal = calculatePotentialLethalDamage(state, ai, opponent)
        if (!lethal.canKillThisTurn && !lethal.canKillByNextDay) {
          s -= 15 // Heavily penalize non-lethal attacks - save red for Pushback/Countering
        }
      }
      break
    case CardType.Heal: {
      const aiDiseaseCount = ai.effectCards.filter(c => c.type === CardType.Disease).length
      if (aiDiseaseCount > 0) { s = 30 + aiDiseaseCount * 10 + weights.healBonus } else { s = (ai.maxHealth - ai.health) / 2 + weights.healBonus }
      // Turn-order bonus: going second makes Heal more valuable
      if (state && state.firstPlayerIdForNight !== ai.id) s += 6
      break
    }
    case CardType.Disease: {
      const opponentDiseaseCount = opponent.effectCards.filter(c => c.type === CardType.Disease).length
      const opponentNaturalGripCount = opponent.effectCards.filter(c => c.type === CardType.NaturalGrip).length
      const dotDamage = opponentDiseaseCount + (opponentNaturalGripCount * 2) + 1
      s = 12 + opponentDiseaseCount * 8
      if (dotDamage >= opponent.health) s += 40
      const opponentHasHeal = opponent.hand.some(c => c.type === CardType.Heal || c.typeB === CardType.Heal) ||
        opponent.prepared.some(pc => getCardEffect(pc).type === CardType.Heal)
      if (!opponentHasHeal) s += 10
      if (state && state.round <= 3) s += 8
      if (ai.effectCards.some(c => c.type === CardType.Hibernation && c.appliedRound === state?.round)) s += 8
      // Turn-order bonus: going second makes Disease more valuable
      if (state && state.firstPlayerIdForNight !== ai.id) s += 6
      break
    }
    case CardType.PsySearch: s = 7; break
    case CardType.Steal:
      s = 8
      if (opponent.playerClass === 'MAGE') s += 12 // MAGE counter: Steal their essence/cards
      break
    case CardType.Energize:
      s = 6 + weights.essenceBonus;
      if (opponent.playerClass === 'MAGE') s -= 4; // Mage Balance: Less focus on essence gain
      break
    case CardType.Shock:
      s = 7;
      if (opponent.playerClass === 'MAGE') s += 5; // Mage Balance: More focus on disrupting their essence
      break
    case CardType.Hibernation:
      s = 5
      // SHANU Survival Strategy: Higher HP threshold (10 instead of 8) and more bonuses
      if (isShanu && ai.health <= 10) {
        s += 25
        // Chaining bonus (2-night streak)
        if (ai.effectCards.some(c => c.type === CardType.Hibernation && c.appliedRound === (state?.round || 0) - 1)) {
          s += 15
        }
        // Protection bonus
        if (ai.prepared.some(pc => [CardType.DoubleCross, CardType.PsyBlock].includes(getCardEffect(pc).type))) {
          s += 10
        }
      }
      // General Defense: aware of both hand and board
      const boardThreat = opponentPreparedTypes.has(CardType.Attack) ||
        opponentPreparedTypes.has(CardType.EnergySword) ||
        opponentPreparedTypes.has(CardType.FlyingScythe) ||
        opponentPreparedTypes.has(CardType.Chromabow) ||
        opponentPreparedTypes.has(CardType.PlantedBomb);

      const handThreat = opponentHandTypes.has(CardType.Attack) || opponentHandHasArtifact || opponentHandHasSpell;

      if (boardThreat) s += 15
      else if (handThreat) s += 10

      if (hasStashPlan) s += 4
      break
    case CardType.Dispel:
      s = 8
      if (opponentHandHasSpell) s += 8
      if (hasStashPlan) s += 4
      break
    case CardType.Pushback:
      s = 12  // Increased base priority for Pushback (counter utility)
      if (opponentHandHasArmory) s += 10  // Strong vs Armory 
      if (hasStashPlan) s += 6
      // Extra bonus if opponent has prepared stash cards
      const opponentHasPreparedStash = opponent.prepared.some(pc => !!pc.card.essenceCost)
      if (opponentHasPreparedStash) s += 12  // High value counter
      break
    case CardType.DoubleCross: s = 7; break
    case CardType.PsyBlock:
      s = 10
      if (opponentHandHasArtifact) s += 10
      if (hasStashPlan) s += 4
      break
    case CardType.Defense:
      s = 8 + value + weights.defenseBonus
      if (opponentHandTypes.has(CardType.Attack)) s += 6
      if (hasStashPlan) s += 5
      break
    case CardType.StealTower:
      // If already captured a tower this night, card is useless
      if (ai.capturedTowerThisTurn) {
        s = -50;
      } else {
        s = 15;
      }
      break
    case CardType.BodyUpgrade: s = 14; break
    case CardType.FortunesFavor: s = 6; break
    case CardType.CursedIron:
      s = 8
      if (opponent.playerClass === 'MAGE') s += 5
      break
    case CardType.ArcaneBoots: s = 3; break
    case CardType.BrokenSecret: s = 5; break
    case CardType.Chromabow: s = 18; break
    case CardType.SealOfSilence:
      s = 3
      if (opponent.playerClass === 'MAGE') s += 3
      break
    case CardType.PlantedBomb:
      s = 18;
      const opponentStashCount = opponent.prepared.filter(pc => !!pc.card.essenceCost).length;
      s += opponentStashCount * 5;
      break;
    case CardType.DiscerningEye:
      s = 14;
      if (ai.hand.length < 3) s += 5;
      break;
    case CardType.MadnessControl:
      s = 6;
      break;
    case CardType.EyesOfMadness:
      s = 16;
      break;
    case CardType.VoodooDoll:
      s = 4;
      break;
  }

  // === DYNAMIC DEFENSE MODIFIERS ===
  if (state && isDefensiveStash(card)) {
    const round = state.round
    const earlyGame = round <= 3
    // Threat detection: Incoming damage OR Opponent has prepared aggressive cards
    const incomingDamage = estimateIncomingDamage(state, opponent.id, ai.id)
    const opponentHasAggressiveStash = opponent.prepared.some(pc =>
      !!pc.card.essenceCost && isAggressiveStash(pc.card)
    )
    const isThreat = incomingDamage > 0 || opponentHasAggressiveStash

    let defenseModifier = 0

    // Logic based on User Request:
    // Case 1: No Threat (Safe)
    if (!isThreat) {
      if (earlyGame) {
        defenseModifier = -6 // "Ранняя игра + Нет входящего урона: Штраф -6"
      } else {
        defenseModifier = -3 // "Нет входящего урона и не ранняя игра: Штраф -3"
      }
    }
    // Case 2: Threat Exists (Aggressive Opponent / Incoming Damage)
    else {
      if (earlyGame) {
        // "Ранняя игра + есть входящий урон: бонус +3 (за подготовку) и -3 за раннюю игру" => Net 0
        defenseModifier = 0
      } else {
        // "Поздняя игра + есть входящий урон: бонус +3"
        defenseModifier = 3
      }
    }

    s += defenseModifier
  }

  return s;
}

export const runAIPreparationTurn = (state: GameState, playerId?: number): { newState: GameState, sequence: { action: AIAction, stateBefore: GameState }[] } => {
  log('aiPrep', 'Running AI Preparation Turn...')
  let newState = JSON.parse(JSON.stringify(state))
  let ai = playerId !== undefined
    ? newState.players.find((p: any) => p.id === playerId)
    : (newState.players.find((p: any) => p.isAI) || newState.players[1]);
  if (!ai) return { newState, sequence: [] };
  const sequence: { action: AIAction, stateBefore: GameState }[] = [];
  const captureState = () => {
    // Create a snapshot of the current state for training
    // Ensure the 'ai' object in newState is up-to-date (it is, as 'ai' is a reference to newState.players[1]?)
    // Wait, 'let ai = newState.players[1]' gets a reference to the object in the array?
    // JS objects are references. Yes.
    // But we need to be sure we deep copy the WHOLE newState for the snapshot.
    return JSON.parse(JSON.stringify(newState));
  };

  const opponent = newState.players.find(p => p.id !== ai.id)!
  const hasStashPlan = ai.hand.some(c => !!c.essenceCost) || ai.prepared.some(pc => !!pc.card.essenceCost)
  const needCounterColor = requiredCounterColorForClass(opponent.playerClass)
  const opponentPreparedTypes = new Set<CardType>(opponent.prepared.map(pc => getCardEffect(pc).type))
  const opponentHandTypes = new Set(opponent.hand.flatMap(c => [c.type, ...(c.typeB ? [c.typeB] : [])]))
  const opponentHandHasArmory = opponent.hand.some(c => c.subType === 'Armory')
  const opponentHandHasSpell = opponent.hand.some(c => c.subType === 'Spell')
  const opponentHandHasArtifact = opponent.hand.some(c => c.subType === 'Artifact')
  const availableSlots = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length
  if (availableSlots <= 0) {
    log('aiPrep', 'No available slots for AI to prepare cards.')
    return { newState, sequence }
  }

  // Calculate raw scores (ignoring cost for potential) to identify "Dream Cards"
  const prelimScoredHand = ai.hand.map(card => {
    const scoreA = scoreCardForAI(card, ai, opponent, opponentPreparedTypes)
    const scoreB = card.typeB ? scoreCardForAI({ ...card, type: card.typeB, value: card.valueB! }, ai, opponent, opponentPreparedTypes) : -1
    return { card, score: Math.max(scoreA, scoreB) }
  })

  // Check if we have high-score cards we can't afford
  const neededEssence = prelimScoredHand.reduce((acc, item) => {
    if (item.card.essenceCost) {
      const cost = getEffectiveEssenceCostForPlayer(item.card, ai);
      if (ai.essence < cost) return Math.max(acc, cost - ai.essence);
    }
    return acc;
  }, 0);

  const rawScoredHand = ai.hand.map(card => {
    let score = 0
    let bestRotation = 0
    const scoreA = scoreCardForAI(card, ai, opponent, opponentPreparedTypes)
    const scoreB = card.typeB ? scoreCardForAI({ ...card, type: card.typeB, value: card.valueB! }, ai, opponent, opponentPreparedTypes) : -1
    if (scoreA >= scoreB) { score = scoreA; bestRotation = 0 } else { score = scoreB; bestRotation = 180 }

    // Stash/Essence Logic
    if (card.essenceCost) {
      score += 12; // Massive Bonus for being a powerful stash card to burn at night
      if (neededEssence > 0 && score < 20) {
        score += 8; // Extra bonus as essence fodder if we need essence for other cards
      }
    }

    // Counter-color penalty: reduce score but don't nullify
    // AI should still prepare cards, just prefer other colors when possible
    if (needCounterColor && card.color === needCounterColor && !ai.towers.some(t => t.color === needCounterColor)) {
      // Only apply penalty to NON-stash cards (stash cards are valuable regardless)
      if (!card.essenceCost) {
        score -= 8; // Moderate penalty, not complete nullification
      }
    }

    return { card, score, rotation: bestRotation, rawScore: score }
  })

  // Sort by potential score
  rawScoredHand.sort((a, b) => b.rawScore - a.rawScore)

  // Cumulative budgeting
  let currentEssence = ai.essence;
  const cardsToPrepare: { card: Card, score: number, rotation: number, rawScore: number }[] = [];

  // === ANTI-PASSIVITY RULE 1: Force RED Attack / GREEN Disease at 3+ ===
  const redAttacks = ai.hand.filter(c => c.color === CardColor.Red && c.type === CardType.Attack && !c.essenceCost);
  const greenDiseases = ai.hand.filter(c => c.color === CardColor.Green && c.type === CardType.Disease && !c.essenceCost);

  const forcedCards: { card: Card, rotation: number }[] = [];
  if (redAttacks.length >= 3) {
    // Force prepare up to 2 RED attack cards
    const toForce = redAttacks.slice(0, 2);
    toForce.forEach(c => forcedCards.push({ card: c, rotation: 0 }));
  }
  if (greenDiseases.length >= 3) {
    // Force prepare up to 2 GREEN disease cards
    const toForce = greenDiseases.slice(0, 2);
    toForce.forEach(c => forcedCards.push({ card: c, rotation: 180 })); // Disease is on B-side
  }

  // === ANTI-PASSIVITY RULE 3: Force Damage Stash at 3+ damage cards ===
  const isDamageDealingType = (type: CardType): boolean => [
    CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
    CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
    CardType.Chromabow, CardType.PlantedBomb, CardType.Counterblast, CardType.Trembling
  ].includes(type);

  const damageCardsInHand = ai.hand.filter(c => isDamageDealingType(c.type)).length;
  const damageStashCards = ai.hand.filter(c => c.essenceCost && isDamageDealingType(c.type));

  if (damageStashCards.length > 0 && damageCardsInHand >= 3) {
    // Force prepare one damage stash card
    const bestDamageStash = damageStashCards.sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    if (!forcedCards.some(fc => fc.card.id === bestDamageStash.id)) {
      forcedCards.push({ card: bestDamageStash, rotation: 0 });
    }
  }

  // === ANTI-PASSIVITY RULE 4: Occultist Artifact Accumulation ===
  const isOccultist = ai.playerClass === 'OCCULTIST';
  if (isOccultist) {
    const artifactsInHand = ai.hand.filter(c => c.subType === 'Artifact');
    if (artifactsInHand.length >= 2) {
      // Force prepare the best one
      const bestArtifact = artifactsInHand.sort((a, b) => (b.essenceCost || 0) - (a.essenceCost || 0))[0];
      if (!forcedCards.some(fc => fc.card.id === bestArtifact.id)) {
        forcedCards.push({ card: bestArtifact, rotation: 0 });
      }
    }
  }

  // Add forced cards first
  const forcedCardIds = new Set<string>();
  for (const fc of forcedCards) {
    const currentAvailableSlots = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length - cardsToPrepare.length;
    if (currentAvailableSlots <= 0) break;
    if (!ai.hand.some(c => c.id === fc.card.id)) continue;
    forcedCardIds.add(fc.card.id);
    cardsToPrepare.push({ card: fc.card, score: 100, rotation: fc.rotation, rawScore: 100 });
  }

  // Then add scored cards (excluding already forced ones)
  for (const item of rawScoredHand) {
    // DYNAMIC SLOT RECALCULATION: Warrior's Armory bonus is applied after preparing an Armory
    // So we must recalculate available slots each iteration
    const currentAvailableSlots = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length - cardsToPrepare.length;
    if (currentAvailableSlots <= 0) break;

    if (!ai.hand.some(c => c.id === item.card.id)) continue;
    if (forcedCardIds.has(item.card.id)) continue; // Already added as forced

    // NOTE: PREPARATION IS FREE! Essence is only required at ACTIVATION time.
    // AI can prepare stash cards even without essence, then BURN them at night for +4 essence each.
    // So we DO NOT check essence cost here anymore.
    // The old code was blocking stash card preparation which caused AI to hoard cards indefinitely.

    // Track how much essence we EXPECT to need for activations (for planning only)
    // But still allow preparation regardless
    if (item.card.essenceCost) {
      const cost = getEffectiveEssenceCostForPlayer(item.card, ai);
      // Don't skip - just track for planning. Card can be burned for essence instead of activated.
      if (currentEssence >= cost) {
        currentEssence -= cost; // Reserve essence for potential activation
      }
      // If we can't afford, still prepare it - we can burn it at night for +4 essence!
    }

    cardsToPrepare.push(item);
  }

  // FALLBACK: If cardsToPrepare is empty but AI has cards, prepare SOMETHING
  // This prevents AI from hoarding 30+ cards and never attacking
  // Rule: If we have room, we MUST fill it if we have cards.
  const slotsRemaining = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length - cardsToPrepare.length;
  if (slotsRemaining > 0 && ai.hand.length > cardsToPrepare.length) {
    const alreadyPlanning = new Set(cardsToPrepare.map(p => p.card.id));
    const leftovers = rawScoredHand
      .filter(item => !alreadyPlanning.has(item.card.id))
      .map(item => {
        let bonus = 0;
        if (isOccultist && item.card.subType === 'Artifact') bonus += 5;
        if (ai.playerClass === 'MAGE' && item.card.subType === 'Spell') bonus += 5;
        return { ...item, score: item.score + bonus };
      })
      .sort((a, b) => b.score - a.score);

    for (let i = 0; i < Math.min(slotsRemaining, leftovers.length); i++) {
      cardsToPrepare.push({ ...leftovers[i], score: Math.max(1, leftovers[i].score) });
    }
  }

  // ESSENCE EMERGENCY: If AI has 0 essence and has stash cards in hand but none prepared,
  // force prepare at least one stash card for burning next night (essence generation)
  const preparedStashCount = ai.prepared.filter(pc => pc.card.essenceCost).length +
    cardsToPrepare.filter(item => item.card.essenceCost).length;
  const handStashCards = ai.hand.filter(c => c.essenceCost && !cardsToPrepare.some(p => p.card.id === c.id));
  const finalSlotsRemaining = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length - cardsToPrepare.length;

  if (ai.essence < 6 && preparedStashCount === 0 && handStashCards.length > 0 && finalSlotsRemaining > 0) {
    // Prepare the cheapest stash card as essence fodder
    const fodderCard = handStashCards.sort((a, b) => (a.essenceCost || 0) - (b.essenceCost || 0))[0];
    cardsToPrepare.push({ card: fodderCard, score: 15, rotation: 0, rawScore: 15 });
    log('aiPrep', `ESSENCE EMERGENCY: Preparing ${fodderCard.type} as essence fodder (${ai.essence} essence, no stash prepared)`);
  }


  cardsToPrepare.forEach(item => {

    const stateBefore = captureState();

    // Do NOT deduct essence from 'ai.essence' here. 
    // Preparation is free; the cost is paid at activation time in the Engine.
    // We only tracked it above ('currentEssence') to ensure we don't prepare more than we can afford.

    ai.prepared.push({ card: item.card, rotation: item.rotation })
    ai.hand = ai.hand.filter(c => c.id !== item.card.id)
    const effect = getCardEffect({ card: item.card, rotation: item.rotation })

    sequence.push({ action: { type: 'PREPARE_CARD', cardId: item.card.id, rotation: item.rotation, priority: item.rawScore }, stateBefore });
    log('aiPrep', `AI is preparing ${effect.type} (rotation: ${item.rotation})`)
  })

  // === WARRIOR ARMORY BONUS: Dynamic slot recalculation ===
  // After preparing Armory, Warrior gets +1 slot. Check if we can prepare more cards.
  if (ai.playerClass === 'WARRIOR') {
    let extraSlotsAvailable = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length;

    while (extraSlotsAvailable > 0 && ai.hand.length > 0) {
      // Find best remaining card to prepare
      const remainingCards = ai.hand.map(card => {
        const scoreA = scoreCardForAI(card, ai, opponent, opponentPreparedTypes);
        const scoreB = card.typeB ? scoreCardForAI({ ...card, type: card.typeB, value: card.valueB! }, ai, opponent, opponentPreparedTypes) : -1;
        return {
          card,
          score: Math.max(scoreA, scoreB),
          rotation: scoreA >= scoreB ? 0 : 180
        };
      }).sort((a, b) => b.score - a.score);

      if (remainingCards.length === 0) break;

      const best = remainingCards[0];

      const stateBefore = captureState();
      ai.prepared.push({ card: best.card, rotation: best.rotation });
      ai.hand = ai.hand.filter(c => c.id !== best.card.id);

      sequence.push({ action: { type: 'PREPARE_CARD', cardId: best.card.id, rotation: best.rotation, priority: best.score }, stateBefore });
      log('aiPrep', `AI (Warrior) preparing extra card from Armory bonus: ${best.card.type}`);

      // Recalculate slots after this preparation (in case another Armory was prepared)
      extraSlotsAvailable = getAvailablePrepareSlots(newState, ai.id, GamePhase.Preparation) - ai.prepared.length;
    }
  }

  // === DYNAMIC RE-PREPARATION (Swap Pass) ===
  // NOTE: We can only SWAP cards, NOT burn them for essence during DAY.
  // Burning Stash cards for +4 essence is ONLY allowed at NIGHT (Combat phase).
  // So the swap logic removes a prepared card (returning it to hand) and
  // prepares a different one from hand instead.

  let swapIterations = 0;
  const MAX_SWAP_ITERATIONS = 2;

  while (swapIterations < MAX_SWAP_ITERATIONS) {
    swapIterations++;
    let bestSwap: { remove: any, add: Card, rotation: number } | null = null;
    let bestDiff = 12;

    for (const pc of ai.prepared) {
      if (pc.lockedState && newState.round - pc.lockedState.appliedRound === 0) continue;

      const currentScore = scoreCardForAI(pc.card, ai, opponent, opponentPreparedTypes, newState);

      for (const handCard of ai.hand) {
        // Only swap Stash cards (they can be unprepared by returning to hand)
        // Non-stash cards cannot be unprepared during the day
        if (!pc.card.essenceCost) continue;

        // Can we afford the new card at night? Check with CURRENT essence (no +4 from burning)
        if (handCard.essenceCost) {
          const cost = getEffectiveEssenceCostForPlayer(handCard, ai);
          if (ai.essence < cost) continue;
        }

        const scoreA = scoreCardForAI(handCard, ai, opponent, opponentPreparedTypes, newState);
        const scoreB = handCard.typeB ? scoreCardForAI({ ...handCard, type: handCard.typeB, value: handCard.valueB! }, ai, opponent, opponentPreparedTypes, newState) : -1;
        const bestNewScore = Math.max(scoreA, scoreB);
        const bestRotation = scoreA >= scoreB ? 0 : 180;

        const diff = bestNewScore - currentScore;

        if (diff > bestDiff) {
          bestDiff = diff;
          bestSwap = { remove: pc, add: handCard, rotation: bestRotation };
        }
      }
    }

    if (bestSwap) {
      const { remove, add, rotation } = bestSwap;

      // 1. Return removed card to hand (NO essence gain during day!)
      const stateBeforeDiscard = captureState();
      ai.hand.push(remove.card); // Return to hand instead of burning
      ai.prepared = ai.prepared.filter(p => p !== remove);

      sequence.push({ action: { type: 'PREPARE_CARD' as const, cardId: remove.card.id, rotation: 0, priority: -1 }, stateBefore: stateBeforeDiscard });
      log('aiPrep', `AI Swapping: Returning ${remove.card.type} to hand (no essence gain during day)`);

      // 2. Prepare new card
      const stateBeforePrep = captureState();
      ai.prepared.push({ card: add, rotation });
      ai.hand = ai.hand.filter(c => c.id !== add.id);

      sequence.push({ action: { type: 'PREPARE_CARD', cardId: add.id, rotation: rotation, priority: 200 }, stateBefore: stateBeforePrep });
      log('aiPrep', `AI Swapping: Preparing ${add.type} (Score Diff: ${bestDiff})`);
    } else {
      break; // No better swaps
    }
  }

  // Fix: Use correct player index instead of hardcoded [1]
  const aiIndex = newState.players.findIndex((p: any) => p.id === ai.id);
  if (aiIndex !== -1) {
    newState.players[aiIndex] = ai;
  }
  log('aiPrep', 'AI Preparation Turn complete.')
  return { newState, sequence }
}

export const isValidCounter = (counterCard: PreparedCard, targetCard: PreparedCard): boolean => {
  const { type: counterType } = getCardEffect(counterCard)
  const { type: targetType } = getCardEffect(targetCard)
  const targetSubType = targetCard.card.subType

  // Counterblast reflects only immediate incoming damage from the selected source.
  if (counterType === CardType.Counterblast) {
    return canCounterblastTarget(targetCard, counterCard.card.ownerId ?? 0)
  }

  const counterIsSpikes = isSpikesCard(counterCard)

  // All damage-dealing card types
  const allDamageTypes = [
    CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
    CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
    CardType.Chromabow, CardType.Counterblast, CardType.PlantedBomb, CardType.Trembling
  ]

  // Spikes can counter ANY damage-dealing card (regardless of subType)
  if (counterIsSpikes) {
    if (isImmediateOrReflectedDamageSource(targetCard)) return true
    if (isSpikesCard(targetCard)) return true
  }

  if (targetSubType === 'Armory') {
    // Pushback can counter Armory weapons
    if (counterType === CardType.Pushback) return true
    // PlantedBomb can counter any stash card (Armory has essenceCost)
    if (counterType === CardType.PlantedBomb && targetCard.card.essenceCost) return true
    // Hibernation can counter damage-dealing Armory (Spikes, attack weapons)
    if (counterType === CardType.Hibernation) {
      const damageArmory = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.Chromabow
      ]
      // Spikes is Defense type but deals damage
      if (isSpikesCard(targetCard)) return true
      if (damageArmory.includes(targetType)) return true
    }
    return false
  }
  if (targetSubType === 'Spell') {
    // Standard Spell counters
    if (counterType === CardType.Dispel || counterType === CardType.SealOfSilence) return true
    // PlantedBomb can counter any stash card (Spells have essenceCost)
    if (counterType === CardType.PlantedBomb && targetCard.card.essenceCost) return true
    // Hibernation and Invincible can counter damage-dealing Spells
    if (counterType === CardType.Hibernation || counterType === CardType.Invincible) {
      const damageSpells = [CardType.Counterblast, CardType.LifeDrain, CardType.NaturalGrip, CardType.Trembling]
      if (damageSpells.includes(targetType)) return true
    }
    return false
  }
  if (targetSubType === 'Artifact') {
    // PsyBlock can counter Artifacts
    if (counterType === CardType.PsyBlock) return true
    // PlantedBomb can counter any stash card (Artifacts have essenceCost)
    if (counterType === CardType.PlantedBomb && targetCard.card.essenceCost) return true
    // Hibernation can counter damage-dealing Artifacts (PlantedBomb)
    if (counterType === CardType.Hibernation && targetType === CardType.PlantedBomb) return true
    return false
  }
  if (!targetSubType) {
    if (counterType === CardType.Pushback) return true
    if (counterType === CardType.Defense && (targetType === CardType.Attack || targetType === CardType.Counterblast)) return true
    // Hibernation and Invincible can counter damage-dealing colored cards
    if (counterType === CardType.Hibernation || counterType === CardType.Invincible) {
      const damageTypes = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
        CardType.Chromabow, CardType.Counterblast, CardType.PlantedBomb, CardType.Disease
      ]
      // Also allow vs Spikes (Defense/Armory with spikes)
      if (isSpikesCard(targetCard)) return true
      if (damageTypes.includes(targetType)) return true
    }
    // DoubleCross can ONLY counter colored cards (not Stash)
    if (counterType === CardType.DoubleCross) return !targetCard.card.essenceCost;
    if (counterType === CardType.Dispel) {
      const forbiddenColors = [CardColor.Red, CardColor.Pink]
      return !forbiddenColors.includes(targetCard.card.color)
    }
    // SealOfSilence can counter Pink deck cards and Spell stash cards
    if (counterType === CardType.SealOfSilence) {
      const isPink = !targetCard.card.essenceCost && targetCard.card.color === CardColor.Pink
      const isSpell = !!targetCard.card.essenceCost && targetCard.card.subType === 'Spell'
      if (isPink || isSpell) return true
    }
    if (counterType === CardType.PsyBlock) return true
    if (counterType === CardType.Protector) {
      const dmg = [
        CardType.Attack,
        CardType.EnergySword,
        CardType.FlyingScythe,
        CardType.InfernoGauntlet,
        CardType.Larcendagger,
        CardType.LifeDrain,
        CardType.Chromabow,
      ]
      return dmg.includes(targetType)
    }
  }
  return false
}


export const getBestAIAction = (state: GameState, playerId?: number): AIAction => {
  // If playerId is provided, use it directly; otherwise fall back to isAI detection
  const ai = playerId
    ? state.players.find(p => p.id === playerId) ?? state.players[1]
    : state.players.find(p => p.isAI) ?? state.players[1]
  if (!ai) return { type: 'PASS', priority: 0 }
  const opponent = state.players.find(p => p.id !== ai.id) ?? state.players[0]
  if (!opponent) return { type: 'PASS', priority: 0 }

  const isSilencedNow = ai.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === state.round)
  const isBlockedBySilence = (card: Card): boolean => {
    const isPinkColorDeck = !card.essenceCost && card.color === CardColor.Pink
    const isSpellStash = !!card.essenceCost && card.subType === 'Spell'
    return isPinkColorDeck || isSpellStash
  }

  const currentActivation = getTopActivation(state)
  const opponentPreparedTypes = new Set(opponent.prepared.map(pc => getCardEffect(pc).type))
  const opponentPreparedSubTypes = new Set(opponent.prepared.map(pc => pc.card.subType))
  const aiPreparedTypes = new Set(ai.prepared.map(pc => getCardEffect(pc).type))
  const needCounterColor = requiredCounterColorForClass(opponent.playerClass)

  if (currentActivation) {
    // === RESPONSE MODE (Reactive) ===
    const hasActiveActivation = state.activations.some(act => !act.resolved && !act.accepted);
    const isAIsTurnToRespond = hasActiveActivation &&
      state.lastCardPlayerId !== undefined &&
      state.lastCardPlayerId !== ai.id;

    if (isAIsTurnToRespond) {
      // 1. Calculate Threat
      const totalIncomingDamage = estimateIncomingDamage(state, opponent.id, ai.id);
      const isSurvivalSituation = totalIncomingDamage >= ai.health;

      // 2. Identify all possible reactions
      const respondableChains = state.activations
        .map((act, idx) => ({ activation: act, index: idx }))
        .filter(({ activation }) => !activation.resolved && !activation.accepted)
        .filter(({ activation }) => {
          const lastCard = activation.counterChain.length > 0
            ? activation.counterChain[activation.counterChain.length - 1]
            : activation.attackingCard;
          return lastCard.card.ownerId !== ai.id;
        })
        .sort((a, b) => (a.activation.treeDepth ?? 0) - (b.activation.treeDepth ?? 0));

      const allCardsInAllChains = new Set(
        state.activations.flatMap(act => [act.attackingCard.card.id, ...act.counterChain.map(pc => pc.card.id)])
      )

      const potentialCounters: AIAction[] = []
      for (const { activation: targetActivation } of respondableChains) {
        const targetCard =
          targetActivation.counterChain.length > 0
            ? targetActivation.counterChain[targetActivation.counterChain.length - 1]
            : targetActivation.attackingCard

        for (const pc of ai.prepared) {
          if (isSilencedNow && isBlockedBySilence(pc.card)) continue
          if (allCardsInAllChains.has(pc.card.id) || (pc.lockedState && state.round - pc.lockedState.appliedRound === 0)) {
            continue
          }

          const checkReaction = (rotation: number) => {
            const tempPc = { ...pc, rotation }
            const effect = getCardEffect(tempPc)

            // DoubleCross only works if:
            // 1. WE initiated the chain (root activation)
            // 2. We haven't passed our active turn yet
            if (effect.type === CardType.DoubleCross) {
              const rootActivation = state.activations[0]
              const isInitiator = rootActivation?.initiatorId === ai.id
              const hasPassed = ai.hasPassedActiveTurn === true
              if (!isInitiator || hasPassed) return;
            }

            if (isValidCounter(tempPc, targetCard)) {
              let priority = 100

              const isCoril = getEffectiveHero(ai, opponent) === 'CORIL';
              if (isCoril && targetCard.card.essenceCost) priority += 150;

              priority += (10 - (targetActivation.treeDepth ?? 0)) * 5

              const targetEffect = getCardEffect(targetCard)
              const targetDamage = targetEffect.value || 0
              const targetIsStash = !!targetCard.card.essenceCost
              const targetSubType = targetCard.card.subType

              const pushbackScarcity = opponent.playerClass === 'WARRIOR' ? 2.0 :
                opponent.playerClass === 'OCCULTIST' ? 0.5 : 1.0
              const psyBlockScarcity = opponent.playerClass === 'OCCULTIST' ? 2.0 :
                opponent.playerClass === 'WARRIOR' ? 0.5 : 1.0
              const dispelScarcity = opponent.playerClass === 'MAGE' ? 2.0 : 1.0

              let threatValue = targetDamage
              if (targetIsStash) threatValue += 15
              if (targetEffect.type === CardType.Counterblast) threatValue = 35
              if (targetSubType === 'Armory' && (targetCard.card as any).faceSlug === 'spikes') threatValue = 35

              if (effect.type === CardType.Defense) {
                const efficiency = Math.min(1, (effect.value || 0) / Math.max(1, targetDamage))
                priority += Math.round(efficiency * 30)
              } else if (effect.type === CardType.Invincible) {
                if (threatValue <= 2 && !isSurvivalSituation) priority -= 40
                if (threatValue >= 4 || isSurvivalSituation) priority += 50
              } else if (effect.type === CardType.Pushback) {
                if (targetSubType === 'Armory') priority += Math.round(40 * pushbackScarcity)
                else if (opponent.playerClass === 'WARRIOR' && opponent.prepared.some(p => p.card.subType === 'Armory')) priority -= 20
              } else if (effect.type === CardType.PsyBlock) {
                if (targetSubType === 'Artifact') priority += Math.round(40 * psyBlockScarcity)
                else if (opponent.playerClass === 'OCCULTIST' && opponent.hand.some(c => c.subType === 'Artifact')) priority -= 15
                else priority += 10
              } else if (effect.type === CardType.Dispel) {
                if (targetSubType === 'Spell') priority += Math.round(40 * dispelScarcity)
              } else if (effect.type === CardType.Counterblast) {
                priority += 30
              } else if (effect.type === CardType.DoubleCross) {
                priority += 20
              } else if (effect.type === CardType.Hibernation) {
                // Don't play Hibernation if already active this round
                if (ai.effectCards.some(ec => ec.type === CardType.Hibernation && ec.appliedRound === state.round)) {
                  return;
                }
                const isShanu = getEffectiveHero(ai, opponent) === 'SHANU';
                const isLethalDOT = isLethalDOTNextDay(ai);
                const playedHibernationLastTurn = ai.effectCards.some(ec => ec.type === CardType.Hibernation && ec.appliedRound === state.round - 1);
                const hasGreenCard = ai.prepared.some(pc => {
                  const eff2 = getCardEffect(pc)
                  return eff2.type === CardType.Heal
                }) || ai.hand.some(c => c.type === CardType.Heal || c.typeB === CardType.Heal)
                const opponentDamagePrepared = opponent.prepared.filter(pc => {
                  const eff2 = getCardEffect(pc)
                  return damageTypes.includes(eff2.type)
                }).length
                const opponentDamageInHand = opponent.hand.filter(c =>
                  damageTypes.includes(c.type) || (c.typeB && damageTypes.includes(c.typeB))
                ).length

                if (isSurvivalSituation || isLethalDOT) {
                  priority = 290;
                } else if (isShanu && ai.health < 10) {
                  priority = 210;
                  if (playedHibernationLastTurn) priority += 100;
                } else if (ai.health < 10 && ai.hand.length >= 6 && !hasGreenCard && state.round >= 3) {
                  priority = 180;
                } else if ((opponentDamagePrepared + opponentDamageInHand) >= 3 && ai.health <= 10) {
                  priority = 200;
                } else {
                  return;
                }
              }

              if (isSurvivalSituation && effect.type !== CardType.Hibernation) {
                priority = Math.max(priority, 280);
              }

              // Essence Management for Reactions
              if (pc.card.essenceCost) {
                const cost = getEffectiveEssenceCostForPlayer(pc.card, ai)
                if (ai.essence < cost) {
                  const gains = computeEssenceGainOptions(state, ai.id)
                  const need = cost - ai.essence
                  let burnPriority = isSurvivalSituation ? 299 : 100;

                  if (gains.preparedId && need <= 4) {
                    potentialCounters.push({ type: 'DISCARD_STASH', cardId: gains.preparedId, priority: burnPriority });
                    return;
                  }
                  return;
                }
              }

              potentialCounters.push({ type: 'COUNTER_ACTIVATION', cardId: pc.card.id, rotation, priority, targetActivationId: targetActivation.attackingCard.card.id })
            }
          }

          if (pc.lockedState) {
            checkReaction(pc.rotation)
          } else {
            checkReaction(0)
            if (pc.card.typeB) checkReaction(180)
          }
        }

        if (potentialCounters.length > 0) {
          potentialCounters.sort((a, b) => b.priority - a.priority)

          const bestCounter = potentialCounters[0]
          const incomingDmg = estimateIncomingDamage(state, opponent.id, ai.id)
          const shouldAccept = () => {
            if (incomingDmg >= ai.health) return false
            if (isSurvivalSituation) return false

            const damageRatio = incomingDmg / ai.health

            const shieldCount = ai.prepared.filter(pc => {
              const eff = getCardEffect(pc)
              return isShieldEffect(eff.type) || eff.type === CardType.DoubleCross
            }).length

            const opponentStrongThreats = opponent.prepared.filter(pc => {
              const eff = getCardEffect(pc)
              return damageTypes.includes(eff.type) && (eff.value || 0) >= 3
            }).length

            if (damageRatio <= 0.15 && shieldCount <= 2 && opponentStrongThreats >= 1) return true
            if (bestCounter.priority < 120 && damageRatio <= 0.10) return true

            return false
          }

          if (shouldAccept()) {
            log('aiLogic', 'AI accepts damage to save counters');
            return { type: 'ACCEPT_ACTIVATION', priority: 1 }
          }

          log('aiLogic', 'AI has counters, choosing best:', bestCounter);
          return bestCounter
        }
      }

      if (ai.playerClass === 'MAGE' && !isSilencedNow) {
        const mageReactions: AIAction[] = []
        for (const { activation: targetActivation } of respondableChains) {
          const targetCard =
            targetActivation.counterChain.length > 0
              ? targetActivation.counterChain[targetActivation.counterChain.length - 1]
              : targetActivation.attackingCard

          for (const spell of ai.hand) {
            if (spell.subType !== 'Spell' || !spell.essenceCost) continue
            if (ai.essence < getEffectiveEssenceCostForPlayer(spell, ai)) continue
            if (!spell.extendedShield) continue

            const asPrepared: PreparedCard = { card: spell, rotation: 0 }
            if (!isValidCounter(asPrepared, targetCard)) continue

            let priority = 190
            if (isSurvivalSituation) priority = 310
            const eff = getCardEffect(asPrepared)
            if (eff.type === CardType.Invincible) priority += 20
            if (eff.type === CardType.Counterblast) priority += 10

            mageReactions.push({
              type: 'MAGE_CAST_SPELL',
              cardId: spell.id,
              rotation: 0,
              targetId: opponent.id,
              targetActivationId: targetActivation.attackingCard.card.id,
              priority
            })
          }
        }

        if (mageReactions.length > 0) {
          mageReactions.sort((a, b) => b.priority - a.priority)
          return mageReactions[0]
        }
      }

      return { type: 'ACCEPT_ACTIVATION', priority: 1 }
    } else {
      return { type: 'PASS', priority: -1 }
    }
  } else {
    // === PROACTIVE MODE (Initiator) ===
    const potentialActions: AIAction[] = []

    // 1. Calculate State
    const lethalState = calculatePotentialLethalDamage(state, ai, opponent);
    const totalIncomingDamage = estimateIncomingDamage(state, opponent.id, ai.id);
    const isSurvivalSituation = totalIncomingDamage >= ai.health;

    // 2. Tower Logic (High priority but below lethal)
    // Rule: Only one tower per night
    const towersToWin = 5 - ai.towers.length
    if (!ai.capturedTowerThisTurn) {
      const allColors = Object.values(CardColor)
      for (const color of allColors) {
        const usableCards = countUsableColorCards(state, ai.id, color)
        const opponentHasTower = opponent.towers.some(t => t.color === color)
        const neutralAvailable = state.availableTowers.includes(color)
        if (!opponentHasTower && !neutralAvailable) continue
        const cost = opponentHasTower ? 4 : 3

        if (usableCards >= cost) {
          let p = (towersToWin === 1) ? 260 : (color === needCounterColor ? 240 : 200);
          if (getEffectiveHero(ai, opponent) === 'GEMATA') p += 40;

          if (needCounterColor && color === needCounterColor) {
            const remainingAfterCapture = usableCards - cost
            if (remainingAfterCapture === 0 && towersToWin !== 1) {
              p -= 80
            } else if (remainingAfterCapture <= 1) {
              p -= 30
            }
          }

          potentialActions.push({ type: 'PURCHASE_TOWER', color, priority: p })
        }
      }
    }

    // 3. Action Logic (Prepared Cards)
    for (const pc of ai.prepared) {
      if (pc.lockedState && state.round - pc.lockedState.appliedRound === 0) continue
      if (isSilencedNow && isBlockedBySilence(pc.card)) continue

      const checkProactive = (rotation: number) => {
        const effect = getCardEffect({ ...pc, rotation })
        let priority = 0
        let targetId: number | undefined = undefined

        // After passing, only shield cards and playableAfterPass cards can be used
        if (ai.hasPassedActiveTurn && !isShieldEffect(effect.type) && !pc.card.playableAfterPass) {
          return
        }

        // Card logic
        switch (effect.type) {
          case CardType.Attack:
          case CardType.EnergySword:
          case CardType.FlyingScythe:
          case CardType.InfernoGauntlet:
          case CardType.Larcendagger:
          case CardType.LifeDrain:
          case CardType.Chromabow:
            {
              const dmg = Math.max(0, (effect.value || 0) - opponent.shield);
              const outcome = predictCounterChainOutcome(state, ai, opponent, pc);

              // LOW-DAMAGE ATTACK CONSERVATION: Attacks doing ≤1 damage waste Pushback counters.
              // Save red cards for counter reactions (Pushback) unless lethal or late game.
              const isLowDmgAttack = effect.type === CardType.Attack && dmg <= 1;
              const isRedCard = pc.card.color === CardColor.Red;
              if (isLowDmgAttack && !lethalState.canKillThisTurn && !isSurvivalSituation && state.round <= 3) {
                priority = Math.round(15 + dmg * 5);
                break;
              }

              if (outcome.successProbability === 0) {
                // LATE-GAME AGGRESSION: After round 20, attack anyway to break stalemates
                // This forces opponent to use counters and prevents indefinite defensive loops
                if (state.round >= 6 && dmg > 0) {
                  priority = 50 + dmg * 10; // Low but non-zero priority
                } else {
                  priority = 0;
                }
              } else if (dmg >= opponent.health) {
                // KILLER INSTINCT: Immediate lethal
                const opponentCanSurvive = opponent.prepared.some(pc => {
                  const eff = getCardEffect(pc)
                  return [CardType.Defense, CardType.Invincible, CardType.Hibernation, CardType.Counterblast, CardType.Protector, CardType.Pushback, CardType.PsyBlock].includes(eff.type)
                }) || opponent.hand.some(c => {
                  const types = [c.type]
                  if (c.typeB) types.push(c.typeB)
                  return types.some(t =>
                    t === CardType.Defense || t === CardType.Invincible ||
                    t === CardType.Hibernation || t === CardType.Counterblast ||
                    t === CardType.Protector || t === CardType.Pushback ||
                    t === CardType.PsyBlock
                  )
                })
                priority = opponentCanSurvive ? 250 : 999;
              } else if (lethalState.canKillThisTurn) {
                // KILLER INSTINCT: Part of a lethal combo
                const opponentCanSurvive = opponent.prepared.some(pc => {
                  const eff = getCardEffect(pc)
                  return [CardType.Defense, CardType.Invincible, CardType.Hibernation, CardType.Counterblast, CardType.Protector, CardType.Pushback, CardType.PsyBlock].includes(eff.type)
                }) || opponent.hand.some(c => {
                  const types = [c.type]
                  if (c.typeB) types.push(c.typeB)
                  return types.some(t =>
                    t === CardType.Defense || t === CardType.Invincible ||
                    t === CardType.Hibernation || t === CardType.Counterblast ||
                    t === CardType.Protector || t === CardType.Pushback ||
                    t === CardType.PsyBlock
                  )
                })
                priority = opponentCanSurvive ? 250 : 999;
              } else {
                priority = Math.round((280 + outcome.expectedDamage * 5) * outcome.successProbability);
                if (opponentPreparedTypes.has(CardType.Counterblast)) priority = Math.round(priority * 0.3);
              }

              // SET TARGET FOR ATTACKS
              if (priority > 0) {
                targetId = opponent.id;
              }
            }
            break;

          case CardType.Defense:
          case CardType.Invincible:
          case CardType.Frost:
          case CardType.Protector:
            if (isSurvivalSituation) {
              priority = 300;
              if (effect.type === CardType.Invincible) priority = 330;
            } else {
              priority = 0;
            }
            break;

          case CardType.Hibernation:
            {
              // Don't play Hibernation again if already active this round
              const hibernationAlreadyActive = ai.effectCards.some(ec => ec.type === CardType.Hibernation && ec.appliedRound === state.round);
              if (hibernationAlreadyActive) {
                priority = 0;
                break;
              }
              const isShanu = getEffectiveHero(ai, opponent) === 'SHANU';
              const isLethalDOT = isLethalDOTNextDay(ai);
              const playedHibernationLastTurn = ai.effectCards.some(ec => ec.type === CardType.Hibernation && ec.appliedRound === state.round - 1);
              const hasGreenCard = ai.prepared.some(pc => {
                const eff = getCardEffect(pc)
                return eff.type === CardType.Heal
              }) || ai.hand.some(c => c.type === CardType.Heal || c.typeB === CardType.Heal)
              const opponentDamagePrepared = opponent.prepared.filter(pc => {
                const eff = getCardEffect(pc)
                return damageTypes.includes(eff.type)
              }).length
              const opponentDamageInHand = opponent.hand.filter(c =>
                damageTypes.includes(c.type) || (c.typeB && damageTypes.includes(c.typeB))
              ).length

              if (isSurvivalSituation || isLethalDOT) {
                priority = 290;
              } else if (isShanu && ai.health < 10) {
                priority = 210;
                if (playedHibernationLastTurn) priority += 100;
              } else if (ai.health < 10 && ai.hand.length >= 6 && !hasGreenCard && state.round >= 3) {
                priority = 180;
              } else if ((opponentDamagePrepared + opponentDamageInHand) >= 3 && ai.health <= 10) {
                priority = 200;
              } else {
                priority = 0;
              }
            }
            break;

          case CardType.Heal: {
            const aiGoesSecond = state.firstPlayerIdForNight !== ai.id;
            priority = ai.health < ai.maxHealth ? 25 + (ai.maxHealth - ai.health) : 0;
            if (aiGoesSecond && ai.health < ai.maxHealth) priority += 15; // Going second: Heal more valuable
            targetId = ai.id;
            break;
          }

          case CardType.Disease:
          case CardType.NaturalGrip: {
            const oppDiseaseCount = opponent.effectCards.filter(c => c.type === CardType.Disease).length
            const oppNaturalGripCount = opponent.effectCards.filter(c => c.type === CardType.NaturalGrip).length
            const dotDmg = oppDiseaseCount + (oppNaturalGripCount * 2) + (effect.type === CardType.NaturalGrip ? 2 : 1)
            // Turn-order awareness: if AI goes SECOND this night, Disease/Heal are more valuable
            // because AI can accept opponent's Disease then Heal it in its turn
            const aiGoesSecond = state.firstPlayerIdForNight !== ai.id;
            if (dotDmg >= opponent.health) {
              priority = 250
            } else if (state.round >= 4) {
              priority = 160 + dotDmg * 10
            } else {
              priority = 80 + dotDmg * 5
            }
            if (aiGoesSecond) {
              priority += 20; // Going second: Disease/Heal are more valuable
            } else {
              priority -= 10; // Going first: lower priority (play attacks first instead)
            }
            targetId = opponent.id;
            break
          }

          case CardType.Steal:
            priority = opponent.hand.length > 0 ? 24 : 0;
            break;

          case CardType.PsySearch:
            priority = ai.hand.length < 6 ? 23 : 0;
            break;

          case CardType.StealTower:
            if (ai.capturedTowerThisTurn) { priority = 0; break }
            priority = (opponent.towers.length > 0 || state.availableTowers.length > 0) ? 48 : 0;
            if (towersToWin <= 1 && (opponent.towers.length > 0 || state.availableTowers.length > 0)) priority = 260;
            break;
        }

        if (priority > 0) {
          if (pc.card.essenceCost) {
            const cost = getEffectiveEssenceCostForPlayer(pc.card, ai)
            // Always check affordability first
            if (ai.essence < cost) {
              const MIN_PRIORITY_FOR_ESSENCE_BURN = 40;
              if (priority >= MIN_PRIORITY_FOR_ESSENCE_BURN) {
                const maxPotential = calculateMaxPotentialEssence(state, ai.id)
                if (maxPotential >= cost) {
                  // JUST-IN-TIME: Propose discard to reach this goal
                  const discardAction = pickBestJustInTimeDiscard(state, ai.id, cost - ai.essence, priority + 1)
                  if (discardAction) {
                    potentialActions.push(discardAction)
                  }
                }
              }
              // Cannot afford this card - skip activation
              return
            }
            // STASH CARD PROTECTION CHECK: Can we protect this stash card from counters?
            const myCounters = ai.prepared.filter(pc2 => {
              const eff2 = getCardEffect(pc2)
              return isShieldEffect(eff2.type) || eff2.type === CardType.DoubleCross
            }).length
            const opponentCounters = opponent.prepared.filter(pc2 => {
              const eff2 = getCardEffect(pc2)
              return isShieldEffect(eff2.type) || eff2.type === CardType.DoubleCross
            }).length
            const canProtect = myCounters >= opponentCounters
            const isCheapStash = (cost <= 4)

            if (!canProtect && !isCheapStash && priority < 200) {
              // Can't protect expensive stash card and it's not cheap bait — reduce priority
              priority = Math.round(priority * 0.4)
            } else if (!canProtect && isCheapStash) {
              // Cheap stash: use as bait to exhaust opponent counters (slight priority boost)
              priority += 15
            }
          }
          potentialActions.push({ type: 'ATTEMPT_ACTIVATION', cardId: pc.card.id, rotation, priority, targetId });

        }
      }

      if (pc.lockedState) checkProactive(pc.rotation);
      else {
        checkProactive(0);
        if (pc.card.typeB) checkProactive(180);
      }
    }

    // 4. Hero Specifics and Utilities
    if (ai.playerClass === 'MAGE') {
      const spells = ai.hand.filter(c => c.subType === 'Spell' && c.essenceCost)
      spells.forEach(s => {
        if (isSilencedNow && isBlockedBySilence(s)) return
        const effect = getCardEffect({ card: s, rotation: 0 })
        if (ai.hasPassedActiveTurn && !isShieldEffect(effect.type) && !s.playableAfterPass) return

        if (s.extendedShield) return

        let p = 0
        if (damageTypes.includes(effect.type)) {
          const dmg = Math.max(0, (effect.value || 0) - opponent.shield)
          if (dmg >= opponent.health || lethalState.canKillThisTurn) {
            const opponentCanSurvive = opponent.prepared.some(pc => {
              const eff = getCardEffect(pc)
              return [CardType.Defense, CardType.Invincible, CardType.Hibernation, CardType.Counterblast, CardType.Protector, CardType.Pushback, CardType.PsyBlock].includes(eff.type)
            }) || opponent.hand.some(c => {
              const types = [c.type]
              if (c.typeB) types.push(c.typeB)
              return types.some(t =>
                t === CardType.Defense || t === CardType.Invincible ||
                t === CardType.Hibernation || t === CardType.Counterblast ||
                t === CardType.Protector || t === CardType.Pushback ||
                t === CardType.PsyBlock
              )
            })
            p = opponentCanSurvive ? 250 : 999
          } else {
            // BASE AGGRESSION: Always value damage, even if not lethal
            p = 160 + (dmg * 5)
            // LATE-GAME BOOST: Increase aggression to break stalemates
            if (state.round >= 6) p += 60
          }
        } else if (effect.type === CardType.StealTower) {
          // Skip if already captured a tower this night
          if (ai.capturedTowerThisTurn) return
          if (ai.towers.length >= 4 && (opponent.towers.length > 0 || state.availableTowers.length > 0)) p = 999
          else if (ai.towers.length >= 3 && (opponent.towers.length > 0 || state.availableTowers.length > 0)) p = 200
        } else if (effect.type === CardType.ThunderSprint) {
          if (ai.disruptedSlots >= 3 && ai.health > 6) p = 40
        } else if (effect.type === CardType.PsySearch) {
          if (ai.hand.length <= 2) p = 25
        }

        if (p > 0) {
          const cost = getEffectiveEssenceCostForPlayer(s, ai)
          if (ai.essence < cost && p >= 40) {
            const maxPotential = calculateMaxPotentialEssence(state, ai.id)
            if (maxPotential >= cost) {
              const discardAction = pickBestJustInTimeDiscard(state, ai.id, cost - ai.essence, p + 1)
              if (discardAction) {
                potentialActions.push(discardAction)
              }
            }
          } else if (ai.essence >= cost) {
            // MAGE SPELL PROTECTION CHECK: similar to stash card logic
            const myCounters = ai.prepared.filter(pc2 => {
              const eff2 = getCardEffect(pc2)
              return isShieldEffect(eff2.type) || eff2.type === CardType.DoubleCross
            }).length
            const opponentCounters = opponent.prepared.filter(pc2 => {
              const eff2 = getCardEffect(pc2)
              return isShieldEffect(eff2.type) || eff2.type === CardType.DoubleCross
            }).length
            const canProtect = myCounters >= opponentCounters
            const isCheapSpell = (cost <= 4)

            if (!canProtect && !isCheapSpell && p < 200) {
              p = Math.round(p * 0.5)
            } else if (!canProtect && isCheapSpell) {
              p += 10
            }
            potentialActions.push({ type: 'MAGE_CAST_SPELL', cardId: s.id, rotation: 0, targetId: opponent.id, priority: p })
          }
        }
      })
    }

    if (ai.hero === 'ASTRID') {
      Object.entries(ai.astridCharges || {}).forEach(([color, count]) => {
        if ((count as number) >= 3) {
          const p = lethalState.canKillThisTurn ? 999 : 90;
          potentialActions.push({ type: 'ASTRID_BLAST', color: color as CardColor, targetId: opponent.id, priority: p });
        }
      });
    }

    // 5. Proactive Essence Harvesting (Burning useless stash cards)
    // Only burn proactively if essence is low (< 8)
    if (ai.essence < 8) {
      const stashChoices = ai.prepared.filter(pc => !!pc.card.essenceCost && (!pc.lockedState || state.round - pc.lockedState.appliedRound !== 0));
      if (stashChoices.length > 0) {
        const worstStashId = pickStashToDiscard(state, ai.id);
        if (worstStashId) {
          const worstCard = ai.prepared.find(pc => pc.card.id === worstStashId);
          if (worstCard) {
            const score = scoreCardForAI(worstCard.card, ai, opponent, opponentPreparedTypes, state);
            let burnPriority = 0;
            
            // PRIORITY ADJUSTMENT: Lower burn priority so it doesn't happen unless Pass is the alternative
            if (score <= 10) burnPriority = 95; // Just below pass (100) or low priority actions
            else if (score <= 15 && ai.essence < 4) burnPriority = 90;
            else if (score <= 20 && ai.essence < 2) burnPriority = 70;
            
            if (burnPriority > 0) {
               potentialActions.push({ type: 'DISCARD_STASH', cardId: worstStashId, priority: burnPriority });
            }
          }
        }
      }
    }

    // --- TOWER CHANGE LOGIC ---
    // Proactive Swap: Lack counter-color, it's available in neutral, and we have a card to pay.
    // Defensive Swap: Opponent has 4+ cards of our tower's color.
    if (ai.towers.length > 0 && !state.towerChangeMenu && !ai.changedTowerColorThisTurn && ai.hand.some(c => !c.essenceCost)) {
      let openMenu = false;
      let targetTowerIndex = 0;
      let bestTargetColor: CardColor | null = null;

      // 1. Proactive check
      if (needCounterColor && !ai.towers.some(t => t.color === needCounterColor)) {
        if (state.availableTowers.includes(needCounterColor) && ai.hand.some(c => c.color === needCounterColor && !c.essenceCost)) {
          openMenu = true;
          bestTargetColor = needCounterColor;
          // Find a non-counter tower to sacrifice
          const sacrificeIdx = ai.towers.findIndex(t => t.color !== needCounterColor);
          if (sacrificeIdx !== -1) targetTowerIndex = sacrificeIdx;
        }
      }

      // 2. Defensive check (if not already opening for proactive)
      if (!openMenu) {
        for (let i = 0; i < ai.towers.length; i++) {
          const t = ai.towers[i];
          const opponentCardsOfColor = opponent.hand.filter(c => c.color === t.color).length;
          if (opponentCardsOfColor >= 4) {
            // Threat detected! Change this tower to anything we can pay for
            const potentialColors = Array.from(new Set(ai.hand.filter(c => !c.essenceCost).map(c => c.color)));
            if (potentialColors.length > 0) {
              openMenu = true;
              targetTowerIndex = i;
              bestTargetColor = potentialColors[0]; // Just take first available for now
              break;
            }
          }
        }
      }

      if (openMenu) {
        potentialActions.push({ type: 'OPEN_TOWER_CHANGE_MENU', towerIndex: targetTowerIndex, priority: 60 });
      }
    }

    if (state.towerChangeMenu) {
      const currentTowerColor = ai.towers[state.towerChangeMenu.towerIndex]?.color;
      const needColor = requiredCounterColorForClass(opponent.playerClass);
      const availablePaymentColors = Array.from(new Set(ai.hand.filter(c => !c.essenceCost).map(c => c.color)));

      let finalColor: CardColor = availablePaymentColors[0] || CardColor.Red;
      if (needColor && availablePaymentColors.includes(needColor)) {
        finalColor = needColor;
      }

      if (finalColor !== currentTowerColor) {
        potentialActions.push({ type: 'CHANGE_TOWER_COLOR', color: finalColor, towerIndex: state.towerChangeMenu.towerIndex, priority: 200 });
      }
    }


    // 6. Attack Sequencing Rules (Phase 2)
    const opponentHasCounters = opponentPreparedTypes.has(CardType.Defense) ||
      opponentPreparedTypes.has(CardType.Pushback) ||
      opponentPreparedTypes.has(CardType.Invincible) ||
      opponentPreparedTypes.has(CardType.Hibernation) ||
      opponentPreparedTypes.has(CardType.Counterblast) ||
      opponentPreparedTypes.has(CardType.Protector) ||
      opponent.hand.some(c => {
        const types = [c.type]
        if (c.typeB) types.push(c.typeB)
        return types.some(t =>
          t === CardType.Defense || t === CardType.Pushback ||
          t === CardType.Invincible || t === CardType.Hibernation ||
          t === CardType.Counterblast || t === CardType.Protector
        )
      })

    const utilityTypes = new Set([
      CardType.Disease, CardType.NaturalGrip, CardType.Steal,
      CardType.PsySearch, CardType.Shock, CardType.Energize,
      CardType.Dispel, CardType.DoubleCross, CardType.CursedIron,
      CardType.PlantedBomb, CardType.StealTower, CardType.BrokenSecret,
      CardType.BodyUpgrade, CardType.FortunesFavor, CardType.ArcaneBoots,
      CardType.DiscerningEye, CardType.MadnessControl, CardType.EyesOfMadness,
      CardType.VoodooDoll, CardType.SealOfSilence, CardType.Purification,
      CardType.CardCyclone, CardType.MageTower, CardType.Heal
    ])

    const isAttackAction = (a: AIAction): boolean => {
      if (a.type !== 'ATTEMPT_ACTIVATION') return false
      const pc = ai.prepared.find(p => p.card.id === a.cardId)
      if (!pc) return false
      const eff = getCardEffect({ ...pc, rotation: a.rotation ?? 0 })
      return damageTypes.includes(eff.type)
    }

    const isUtilityAction = (a: AIAction): boolean => {
      if (a.type !== 'ATTEMPT_ACTIVATION') return false
      const pc = ai.prepared.find(p => p.card.id === a.cardId)
      if (!pc) return false
      const eff = getCardEffect({ ...pc, rotation: a.rotation ?? 0 })
      return utilityTypes.has(eff.type)
    }

    const isLethalAction = (a: AIAction): boolean => {
      if (!isAttackAction(a)) return false
      if (a.priority >= 999) return true
      return false
    }

    const attackActions = potentialActions.filter(a => isAttackAction(a) && !isLethalAction(a))
    const utilityActions = potentialActions.filter(a => isUtilityAction(a))

    if (opponentHasCounters && attackActions.length > 0 && utilityActions.length > 0) {
      for (const a of potentialActions) {
        if (isUtilityAction(a)) a.priority += 30
        if (isAttackAction(a) && !isLethalAction(a)) a.priority -= 30
      }
    }

    if (opponentHasCounters && attackActions.length >= 2) {
      const sortedAttacks = attackActions.sort((a, b) => a.priority - b.priority)
      const probeAction = sortedAttacks[0]
      probeAction.priority = Math.min(probeAction.priority + 40, 295)
    }

    if (opponent.health >= 12 && state.round <= 2) {
      for (const a of potentialActions) {
        if (isAttackAction(a) && !isLethalAction(a)) a.priority -= 40
      }
    }

    // 7. Strategic Pass (Phase 7)
    const counterCards = ai.prepared.filter(pc => {
      const eff = getCardEffect(pc)
      return isShieldEffect(eff.type) || eff.type === CardType.DoubleCross
    }).length

    const proactiveCards = ai.prepared.filter(pc => {
      const eff = getCardEffect(pc)
      return !isShieldEffect(eff.type) && eff.type !== CardType.DoubleCross
    }).length

    if (
      !ai.hasPassedActiveTurn &&
      !lethalState.canKillThisTurn &&
      !isSurvivalSituation &&
      opponent.prepared.length >= 2 &&
      counterCards > proactiveCards &&
      counterCards >= 2
    ) {
      const bestProactivePriority = potentialActions
        .filter(a => a.type === 'ATTEMPT_ACTIVATION' || a.type === 'MAGE_CAST_SPELL')
        .reduce((max, a) => Math.max(max, a.priority), 0)
      if (bestProactivePriority < 120) {
        potentialActions.push({ type: 'PASS', priority: 150 })
      }
    }

    // 8. Final Selection
    if (potentialActions.length === 0) return { type: 'PASS', priority: 100 };
    potentialActions.sort((a, b) => b.priority - a.priority);
    log('aiLogic', 'AI choosing proactive action:', potentialActions[0]);
    return potentialActions[0];
  }
}
// === NEW AI PHASE HANDLERS ===

export function getAIPhaseAction(state: GameState, aiPlayerId: number): any | null {
  if (!state.players) return null;
  const ai = state.players.find(p => p.id === aiPlayerId);
  if (!ai) return null;

  switch (state.phase) {
    case GamePhase.AWAITING_BURGLARY_CHOICE:
      return chooseBurglaryTarget(state, aiPlayerId);
    case GamePhase.AWAITING_CHROMABOW_CHOICE:
      return chooseChromabowColor(state, aiPlayerId);
    case GamePhase.AWAITING_PURIFICATION_TARGET:
      const targetColor = getOptimalTowerColor(state, aiPlayerId, true);
      return { type: 'CHOOSE_PURIFICATION_TARGET', targetColor, priority: 100 };
    case GamePhase.LorianClassChoice:
      return chooseLorianClassChoice(state, aiPlayerId);
    case GamePhase.BROKEN_SECRET_REVEAL:
      return { type: 'COMPLETE_BROKEN_SECRET', priority: 100 };
    case GamePhase.LockedCardOverflow:
      if (state.overflowPlayerId !== aiPlayerId) return null;
      const cardId = chooseLockedCardToReturnFromOverflow(state, aiPlayerId);
      if (!cardId) return null;
      return { type: 'CHOOSE_LOCKED_CARD_RETURN', cardId, priority: 100 };
    default:
      return null;
  }
}

function chooseBurglaryTarget(state: GameState, aiPlayerId: number): any {
  const ai = state.players.find(p => p.id === aiPlayerId)!;
  const opponent = state.players.find(p => p.id !== aiPlayerId);
  if (!opponent || opponent.hand.length === 0) {
    return { type: 'CHOOSE_BURGLARY_CARD', targetCardId: 'CANCEL', priority: 100 };
  }

  // SMART: Steal the BEST card from opponent's hand (score from opponent's perspective)
  const opponentPreparedTypes = new Set(opponent.prepared.map(pc => getCardEffect(pc).type));
  const scoredCards = opponent.hand.map(c => ({
    card: c,
    // Score how valuable this card is TO THE OPPONENT
    score: scoreCardForAI(c, opponent, ai, opponentPreparedTypes)
  })).sort((a, b) => b.score - a.score);

  log('aiBurglary', `Stealing best card: ${scoredCards[0]?.card.type} (score: ${scoredCards[0]?.score})`);
  return { type: 'CHOOSE_BURGLARY_CARD', targetCardId: scoredCards[0].card.id, priority: 100 };
}

function chooseChromabowColor(state: GameState, aiPlayerId: number): any {
  const ai = state.players.find(p => p.id === aiPlayerId)!;
  const opponent = state.players.find(p => p.id !== aiPlayerId)!;

  // SMART: Choose color based on what AI needs most + what opponent has least of
  // Priority: counter-colors for opponent class > colors AI lacks > colors opponent lacks

  const needCounterColor = requiredCounterColorForClass(opponent.playerClass);
  const aiColorCounts: Record<CardColor, number> = {
    [CardColor.Red]: 0, [CardColor.Green]: 0, [CardColor.Black]: 0,
    [CardColor.Purple]: 0, [CardColor.Yellow]: 0, [CardColor.Pink]: 0
  };
  ai.hand.forEach(c => { if (!c.essenceCost) aiColorCounts[c.color]++ });

  // Filter to available decks
  const availableColors = Object.values(CardColor).filter(color =>
    state.colorDecks[color]?.length > 0
  );

  if (availableColors.length === 0) {
    return { type: 'CHOOSE_CHROMABOW_COLOR', color: CardColor.Red, priority: 100 };
  }

  // Priority 1: Need counter-color for tower capture
  if (needCounterColor && availableColors.includes(needCounterColor) && aiColorCounts[needCounterColor] < 3) {
    log('aiChromabow', `Choosing counter-color: ${needCounterColor}`);
    return { type: 'CHOOSE_CHROMABOW_COLOR', color: needCounterColor, priority: 100 };
  }

  // Priority 2: Color AI has least of (but still useful)
  const sortedByAINeed = availableColors.sort((a, b) => aiColorCounts[a] - aiColorCounts[b]);
  log('aiChromabow', `Choosing color AI lacks: ${sortedByAINeed[0]}`);
  return { type: 'CHOOSE_CHROMABOW_COLOR', color: sortedByAINeed[0], priority: 100 };
}

function chooseLorianClassChoice(state: GameState, aiPlayerId: number): any {
  const opponent = state.players.find(p => p.id !== aiPlayerId)!;

  // SMART: Counter-pick opponent's class
  const counterClass: Record<ClassId, ClassId> = {
    'WARRIOR': 'MAGE',      // Dispel > Armory
    'MAGE': 'OCCULTIST',    // PsyBlock > Spells  
    'OCCULTIST': 'WARRIOR'  // Pushback > Artifacts
  };

  const choice = opponent.playerClass ? counterClass[opponent.playerClass] : 'WARRIOR';
  log('aiLorian', `Counter-picking class: ${choice} (vs ${opponent.playerClass})`);
  return { type: 'CHOOSE_LORIAN_CLASS', classId: choice };
}

// ============================================================================
// AI HERO & CLASS SELECTION LOGIC
// ============================================================================

const COUNTER_PICK_TABLE: Record<HeroId, HeroId> = {
  'ASTRID': 'EROK_BURU',    // 77.8% WR - Astrid cannot penetrate Erok's defense
  'CORIL': 'EVELION',       // 64.3% WR - Evelion consistently outplays Coril
  'EROK_BURU': 'SHANU',     // 62.5% WR - Shanu is the "leader killer", counters Erok aggression
  'EVELION': 'EROK_BURU',   // 78.6% WR - Erok has better raw stats
  'FERRIM': 'EVELION',      // 70.0% WR - Evelion punishes Ferrim's playstyle
  'GEMATA': 'EROK_BURU',    // 64.3% WR - Erok wins the magic duel via raw power
  'LORIAN': 'EROK_BURU',    // 65.0% WR - Lorian is too weak against Erok's domination
  'NORIM': 'CORIL',         // 90.0% WR - Coril destroys Norim (9/10 games)
  'NYRIX': 'CORIL',         // 86.7% WR - Nyrix useless against Coril's control
  'SHANU': 'CORIL',         // 66.7% WR - Coril stops Shanu's aggression
  'TERRIK': 'EROK_BURU',    // 91.7% WR - Terrik has no chance (11/12 losses)
  'VERISK': 'SHANU',        // 78.6% WR - Shanu quickly handles Verisk
  'VERG': 'EROK_BURU',      // Verg is player-only hero, AI counters with Erok
  'TRIFLE': 'EROK_BURU',
  'AXEL': 'CORIL',
}

const DEFAULT_BLIND_PICK: HeroId = 'EROK_BURU';
const BEST_CLASS: ClassId = 'MAGE';

export const getBestHeroAndClass = (state: GameState, aiPlayerId: number): { hero: HeroId, playerClass: ClassId } => {
  const opponentId = aiPlayerId === 1 ? 2 : 1;
  const opponent = state.players.find(p => p.id === opponentId);

  let bestHero: HeroId = DEFAULT_BLIND_PICK;

  // If opponent has already chosen a hero, try to counter-pick
  if (opponent && opponent.hero) {
    const counter = COUNTER_PICK_TABLE[opponent.hero];
    if (counter) {
      bestHero = counter;
      log('aiHero', `AI Counter-Pick: Opponent has ${opponent.hero}, choosing ${bestHero}`);
    } else {
      log('aiHero', `AI Blind Pick (Opponent hero ${opponent.hero} not in table): Choosing ${bestHero}`);
    }
  } else {
    log('aiHero', `AI Blind Pick (First to pick): Choosing ${bestHero}`);
  }

  return { hero: bestHero, playerClass: BEST_CLASS };
}
